import { getEffectiveness } from './typeChart';
import MOVE_TYPES from './moves';
import MOVE_DATA from './moveData';
import { generateDamageMatrix, getEffectiveStats, getEffectiveSpeed, estimateDamage } from './damageCalc';
import { canMegaEvolve, getMegaStats, getMegaTypes, isMegaStone } from './baseStats';
import {
  normalizeStrategy, evaluateTriggers, getBringPrior, shouldAvoidLead,
  findSetupScripts, findKeyOneVOnes,
} from './teamStrategy';

function getMoveType(moveName) {
  if (!(moveName in MOVE_TYPES)) return null;
  return MOVE_TYPES[moveName];
}

function getDamagingMoveTypes(pokemon) {
  if (!pokemon.moves || pokemon.moves.length === 0) return [];
  const types = [];
  for (const move of pokemon.moves) {
    const mt = getMoveType(move);
    if (mt) types.push(mt);
  }
  return [...new Set(types)];
}

function getMoveSet(pokemon) {
  return new Set(pokemon.moves || []);
}

function getOffensiveTypes(pokemon) {
  const damaging = getDamagingMoveTypes(pokemon);
  return damaging.length > 0
    ? [...new Set([...pokemon.types, ...damaging])]
    : pokemon.types;
}

// ============================================================
// Ability classification for scoring
// ============================================================
const ABILITY_TAGS = {
  // Intimidate and stat-drop abilities
  'Intimidate': ['lead', 'pivot', 'physical_counter'],
  // Weather setters — great leads
  'Drought': ['lead', 'weather_sun'], 'Drizzle': ['lead', 'weather_rain'],
  'Sand Stream': ['lead', 'weather_sand'], 'Snow Warning': ['lead', 'weather_snow'],
  // Speed control abilities
  'Chlorophyll': ['speed_sun'], 'Swift Swim': ['speed_rain'],
  'Sand Rush': ['speed_sand'], 'Slush Rush': ['speed_snow'],
  'Speed Boost': ['speed_passive'], 'Unburden': ['speed_conditional'],
  // Priority blocking — excellent leads
  'Armor Tail': ['lead', 'anti_priority'], 'Queenly Majesty': ['lead', 'anti_priority'],
  // Redirection immunity / disruption
  'Prankster': ['lead', 'speed_control'], 'Stalwart': ['anti_redirect'],
  // Pivoting abilities
  'Regenerator': ['pivot'], 'Natural Cure': ['pivot'],
  // Defensive abilities
  'Multiscale': ['lead_tank'], 'Disguise': ['lead_tank'], 'Sturdy': ['lead_tank'],
  'Fur Coat': ['physical_wall'], 'Thick Fat': ['defensive'],
  'Filter': ['defensive'], 'Solid Rock': ['defensive'],
  'Water Absorb': ['immunity_water'], 'Volt Absorb': ['immunity_electric'],
  'Lightning Rod': ['immunity_electric', 'redirect'], 'Flash Fire': ['immunity_fire'],
  'Levitate': ['immunity_ground'], 'Earth Eater': ['immunity_ground'],
  'Dry Skin': ['immunity_water', 'fire_weakness'],
  'Sap Sipper': ['immunity_grass'], 'Motor Drive': ['immunity_electric'],
  // Offensive abilities
  'Huge Power': ['offensive'], 'Pure Power': ['offensive'],
  'Adaptability': ['offensive'], 'Sheer Force': ['offensive'],
  'Tough Claws': ['offensive'], 'Strong Jaw': ['offensive'],
  'Sand Force': ['offensive_sand'], 'Solar Power': ['offensive_sun'],
  'Technician': ['offensive'], 'Mega Launcher': ['offensive'],
  'Guts': ['offensive', 'status_immune_atk'], 'Moxie': ['sweeper'],
  'Supreme Overlord': ['endgame'],
  // Disruption abilities
  'Shadow Tag': ['trapping'], 'Magic Bounce': ['lead', 'anti_status'],
  'Mold Breaker': ['offensive', 'ability_ignore'],
  'Competitive': ['anti_intimidate'], 'Defiant': ['anti_intimidate'],
  'Clear Body': ['anti_intimidate'], 'Inner Focus': ['anti_intimidate', 'anti_flinch'],
  'Mirror Armor': ['anti_intimidate'],
  // Support abilities
  'Hospitality': ['support', 'lead'], 'Friend Guard': ['support'],
  'Fairy Aura': ['support_offensive'],
  // Terrain/field
  'Protean': ['offensive'], 'Pixilate': ['offensive'],
  'Aerilate': ['offensive'], 'Refrigerate': ['offensive'], 'Dragonize': ['offensive'],
};

function getAbilityTags(ability) {
  return ABILITY_TAGS[ability] || [];
}

// ============================================================
// Pokemon scoring against an opponent team
// ============================================================
// Ability-aware defensive type effectiveness. Treats type-chart immunities
// granted by abilities (Levitate, Water Absorb, Flash Fire, etc.) as 0x,
// which is critical for correctly valuing Pokemon like Rotom-Heat (Levitate)
// against Ground-type threats like Garchomp.
function defensiveEffectiveness(attackingType, defenderTypes, abilityTags) {
  if (attackingType === 'Ground' && abilityTags.includes('immunity_ground')) return 0;
  if (attackingType === 'Water' && abilityTags.includes('immunity_water')) return 0;
  if (attackingType === 'Electric' && abilityTags.includes('immunity_electric')) return 0;
  if (attackingType === 'Fire' && abilityTags.includes('immunity_fire')) return 0;
  if (attackingType === 'Grass' && abilityTags.includes('immunity_grass')) return 0;
  return getEffectiveness(attackingType, defenderTypes);
}

// Returns { total, offense, defense, immunity, utility, ability }.
// Per-opponent matchup scoring (offense/defense/immunity) now dominates; the
// always-on utility and ability bonuses used to pile up and push attackers
// like Sneasler above walls like Rotom-Heat regardless of the actual matchup.
function scorePokemonVsTeamBreakdown(pokemon, opponentTeam) {
  const moves = getMoveSet(pokemon);
  const hasMoves = moves.size > 0;
  const offTypes = getOffensiveTypes(pokemon);
  const damagingTypes = getDamagingMoveTypes(pokemon);
  const abilityTags = getAbilityTags(pokemon.ability);

  let offense = 0;
  let defense = 0;
  let immunity = 0;
  let utility = 0;
  let ability = 0;

  for (const opp of opponentTeam) {
    // --- Offensive scoring (per opponent) ---
    let bestOffense = 0;
    for (const atkType of offTypes) {
      bestOffense = Math.max(bestOffense, getEffectiveness(atkType, opp.types));
    }
    if (bestOffense >= 4) offense += 4;
    else if (bestOffense >= 2) offense += 3;
    else if (bestOffense >= 1) offense += 1;
    else if (bestOffense > 0) offense += 0;
    else offense -= 1;

    // Confirmed damaging-move SE bonus — bumped and stackable per opponent
    // since a confirmed SE move is much more reliable than a STAB type read.
    if (hasMoves && damagingTypes.length > 0) {
      for (const mt of damagingTypes) {
        if (getEffectiveness(mt, opp.types) >= 2) { offense += 1; break; }
      }
    }

    // --- Defensive scoring (per opponent) — ability-aware ---
    // worstDefense uses defensiveEffectiveness so ability immunities (Levitate,
    // Water Absorb, Flash Fire, etc.) correctly show as 0x instead of their
    // raw type-chart multiplier. Previously Rotom-Heat scored as 4x weak to
    // Ground vs Garchomp because Levitate was ignored here.
    let worstDefense = 1;
    let hasAbilityImmunity = false;
    for (const oppType of opp.types) {
      const rawEff = getEffectiveness(oppType, pokemon.types);
      const eff = defensiveEffectiveness(oppType, pokemon.types, abilityTags);
      if (eff === 0 && rawEff > 0) hasAbilityImmunity = true;
      worstDefense = Math.max(worstDefense, eff);
    }
    if (worstDefense >= 4) defense -= 5;
    else if (worstDefense >= 2) defense -= 3;
    else if (worstDefense === 0) defense += 4;
    else if (worstDefense <= 0.25) defense += 4;
    else if (worstDefense <= 0.5) defense += 3;

    // --- Ability-based immunity bonus: small extra reward when ability (not
    // type) is what grants the 0x, because it also lets us absorb spread moves
    // and set up safely. The defensive calc above already gives +4 for 0x. ---
    if (hasAbilityImmunity) immunity += 1;
  }

  // --- Ability tag bonuses (always-on, reduced so they don't dominate) ---
  if (abilityTags.includes('pivot')) ability += 0.5;
  if (abilityTags.includes('offensive')) ability += 0.5;
  if (abilityTags.includes('sweeper')) ability += 0.25;
  if (abilityTags.includes('defensive')) ability += 0.25;
  if (abilityTags.includes('support')) ability += 0.25;

  const oppHasIntimidateTypes = opponentTeam.some(o =>
    ['Incineroar', 'Arcanine', 'Gyarados', 'Landorus', 'Salamence', 'Krookodile', 'Luxray'].includes(o.name)
  );
  if (oppHasIntimidateTypes && abilityTags.includes('anti_intimidate')) ability += 2;

  // --- Move utility scoring (halved — these are generic "always good" moves
  // that were drowning out opponent-specific matchups) ---
  if (hasMoves) {
    if (moves.has('Fake Out')) utility += 0.75;
    if (moves.has('Tailwind')) utility += 1;
    if (moves.has('Trick Room')) utility += 1;
    if (moves.has('Follow Me') || moves.has('Rage Powder')) utility += 1;
    if (moves.has('Protect') || moves.has('Detect')) utility += 0.25;
    if (moves.has('Parting Shot') || moves.has('U-turn') || moves.has('Volt Switch') || moves.has('Flip Turn')) utility += 0.5;
    if (moves.has('Will-O-Wisp') || moves.has('Thunder Wave') || moves.has('Glare') || moves.has('Nuzzle')) utility += 0.5;
    if (moves.has('Taunt')) utility += 0.5;
    if (moves.has('Encore')) utility += 0.5;
    if (moves.has('Helping Hand')) utility += 0.25;
    if (moves.has('Icy Wind') || moves.has('Electroweb')) utility += 0.5;
    if (moves.has('Spore') || moves.has('Sleep Powder')) utility += 0.75;
  }

  // Role bonus for preset teams
  if (pokemon.role) {
    const role = pokemon.role.toLowerCase();
    if (role.includes('support') || role.includes('pivot')) ability += 0.5;
    if (role.includes('sweeper') || role.includes('attacker')) ability += 0.25;
  }

  const total = offense + defense + immunity + utility + ability;
  return { total, offense, defense, immunity, utility, ability };
}

function scorePokemonVsTeam(pokemon, opponentTeam) {
  return scorePokemonVsTeamBreakdown(pokemon, opponentTeam).total;
}

// ============================================================
// Lead pair scoring — evaluates how good two Pokemon are together as leads
// ============================================================
function scoreLeadPair(lead1, lead2, opponentTeam) {
  let pairScore = 0;
  const reasons = [];

  const moves1 = getMoveSet(lead1);
  const moves2 = getMoveSet(lead2);
  const tags1 = getAbilityTags(lead1.ability);
  const tags2 = getAbilityTags(lead2.ability);

  // Fake Out + Partner: one disrupts while other acts
  if (moves1.has('Fake Out') || moves2.has('Fake Out')) {
    pairScore += 3;
    const fakeUser = moves1.has('Fake Out') ? lead1.name : lead2.name;
    const partner = moves1.has('Fake Out') ? lead2.name : lead1.name;
    reasons.push(`${fakeUser} Fake Out buys a free turn for ${partner}`);
  }

  // Tailwind/TR setter + offensive threat
  const setter1 = moves1.has('Tailwind') || moves1.has('Trick Room');
  const setter2 = moves2.has('Tailwind') || moves2.has('Trick Room');
  if (setter1 && !setter2) {
    pairScore += 2;
    const speed = moves1.has('Tailwind') ? 'Tailwind' : 'Trick Room';
    reasons.push(`${lead1.name} sets ${speed} while ${lead2.name} attacks`);
  } else if (setter2 && !setter1) {
    pairScore += 2;
    const speed = moves2.has('Tailwind') ? 'Tailwind' : 'Trick Room';
    reasons.push(`${lead2.name} sets ${speed} while ${lead1.name} attacks`);
  }

  // Redirector + setup/sweeper
  const redir1 = moves1.has('Follow Me') || moves1.has('Rage Powder');
  const redir2 = moves2.has('Follow Me') || moves2.has('Rage Powder');
  if (redir1 && !redir2) {
    pairScore += 2;
    reasons.push(`${lead1.name} redirects attacks to protect ${lead2.name}`);
  } else if (redir2 && !redir1) {
    pairScore += 2;
    reasons.push(`${lead2.name} redirects attacks to protect ${lead1.name}`);
  }

  // Priority blocker protects partner
  const blocks1 = tags1.includes('anti_priority');
  const blocks2 = tags2.includes('anti_priority');
  if (blocks1 || blocks2) {
    pairScore += 1.5;
    const blocker = blocks1 ? lead1.name : lead2.name;
    reasons.push(`${blocker} (${blocks1 ? lead1.ability : lead2.ability}) blocks opponent Fake Out`);
  }

  // Weather setter + weather abuser
  const sunSetter = tags1.includes('weather_sun') || tags2.includes('weather_sun');
  const sunAbuser = tags1.includes('speed_sun') || tags2.includes('speed_sun') ||
    tags1.includes('offensive_sun') || tags2.includes('offensive_sun');
  if (sunSetter && sunAbuser) { pairScore += 2.5; reasons.push('Sun setter + Sun abuser combo'); }

  const rainSetter = tags1.includes('weather_rain') || tags2.includes('weather_rain');
  const rainAbuser = tags1.includes('speed_rain') || tags2.includes('speed_rain');
  if (rainSetter && rainAbuser) { pairScore += 2.5; reasons.push('Rain setter + Swift Swim combo'); }

  const sandSetter = tags1.includes('weather_sand') || tags2.includes('weather_sand');
  const sandAbuser = tags1.includes('speed_sand') || tags2.includes('speed_sand') ||
    tags1.includes('offensive_sand') || tags2.includes('offensive_sand');
  if (sandSetter && sandAbuser) { pairScore += 2.5; reasons.push('Sand setter + Sand Rush/Force combo'); }

  const snowSetter = tags1.includes('weather_snow') || tags2.includes('weather_snow');
  const snowAbuser = tags1.includes('speed_snow') || tags2.includes('speed_snow');
  if (snowSetter && snowAbuser) { pairScore += 2.5; reasons.push('Snow setter + Slush Rush combo'); }

  // Intimidate + partner
  if (lead1.ability === 'Intimidate' || lead2.ability === 'Intimidate') {
    pairScore += 1.5;
    const intim = lead1.ability === 'Intimidate' ? lead1.name : lead2.name;
    reasons.push(`${intim} Intimidate weakens physical threats on entry`);
  }

  // Hospitality heals partner
  if (lead1.ability === 'Hospitality' || lead2.ability === 'Hospitality') {
    pairScore += 1;
    const healer = lead1.ability === 'Hospitality' ? lead1.name : lead2.name;
    reasons.push(`${healer} Hospitality heals partner on entry`);
  }

  // Offensive coverage complementation
  const types1 = getOffensiveTypes(lead1);
  const types2 = getOffensiveTypes(lead2);
  let combinedSE = 0;
  for (const opp of opponentTeam) {
    const hit1 = types1.some(t => getEffectiveness(t, opp.types) >= 2);
    const hit2 = types2.some(t => getEffectiveness(t, opp.types) >= 2);
    if (hit1 || hit2) combinedSE++;
  }
  if (combinedSE >= opponentTeam.length) {
    pairScore += 2;
    reasons.push('Together they hit every opponent super effectively');
  } else if (combinedSE >= opponentTeam.length - 1) {
    pairScore += 1;
    reasons.push(`Combined SE coverage on ${combinedSE}/${opponentTeam.length} opponents`);
  }

  // Defensive type synergy — they don't share weaknesses
  const weak1 = new Set();
  const weak2 = new Set();
  const allTypes = ['Normal','Fire','Water','Electric','Grass','Ice','Fighting','Poison','Ground','Flying','Psychic','Bug','Rock','Ghost','Dragon','Dark','Steel','Fairy'];
  for (const t of allTypes) {
    if (getEffectiveness(t, lead1.types) >= 2) weak1.add(t);
    if (getEffectiveness(t, lead2.types) >= 2) weak2.add(t);
  }
  const sharedWeaknesses = [...weak1].filter(t => weak2.has(t));
  if (sharedWeaknesses.length === 0) {
    pairScore += 1.5;
    reasons.push('No shared type weaknesses — hard to exploit together');
  } else if (sharedWeaknesses.length >= 3) {
    pairScore -= 1;
    reasons.push(`Shared weaknesses: ${sharedWeaknesses.join(', ')} — vulnerable to same threats`);
  }

  // Penalty: both are Fake Out users (redundant)
  if (moves1.has('Fake Out') && moves2.has('Fake Out')) {
    pairScore -= 1;
    reasons.push('Both have Fake Out — redundant, only one can use it turn 1');
  }

  // Penalty: both are pure support with no offensive pressure
  const dmg1 = getDamagingMoveTypes(lead1).length;
  const dmg2 = getDamagingMoveTypes(lead2).length;
  if (dmg1 === 0 && dmg2 === 0 && lead1.moves?.length > 0 && lead2.moves?.length > 0) {
    pairScore -= 2;
    reasons.push('Neither lead has damaging moves — no offensive pressure');
  }

  return { pairScore, reasons };
}

// ============================================================
// Team synergy detection — Pokemon whose value only shows when paired with
// a specific teammate (Baton Pass passers, redirect-into-setup sweepers,
// Trick Room + slow hitter, etc.). Without this, individual per-Pokemon
// scoring rates them on raw offense/defense and they never get selected.
// ============================================================
const SETUP_MOVES = new Set([
  'Calm Mind', 'Nasty Plot', 'Swords Dance', 'Dragon Dance', 'Quiver Dance',
  'Bulk Up', 'Iron Defense', 'Agility', 'Rock Polish', 'Cosmic Power',
  'Growth', 'Shell Smash', 'Shift Gear', 'Clangorous Soul',
]);

function applyTeamSynergyBonuses(scored) {
  const byName = new Map(scored.map(s => [s.pokemon.name, s]));
  const addSynergy = (name, delta, label) => {
    const entry = byName.get(name);
    if (!entry) return;
    entry.score += delta;
    entry.synergyBonus = (entry.synergyBonus || 0) + delta;
    entry.synergyReasons = entry.synergyReasons || [];
    entry.synergyReasons.push(label);
  };
  // Record a "must bring together" relationship. Used by the selection pass
  // to pair-lock combos — if one member is picked, the other must be too.
  const linkPartners = (a, b) => {
    const ea = byName.get(a); const eb = byName.get(b);
    if (!ea || !eb || a === b) return;
    ea.synergyPartners = ea.synergyPartners || new Set();
    eb.synergyPartners = eb.synergyPartners || new Set();
    ea.synergyPartners.add(b);
    eb.synergyPartners.add(a);
  };

  // --- Baton Pass combo ---
  // Passer: has Baton Pass + at least one setup move.
  // Receiver: either uses Stored Power (scales with boosts) or has setup moves
  // but NOT Baton Pass (a real sweeper that will consume the boosts).
  const passers = scored.filter(s => {
    const moves = getMoveSet(s.pokemon);
    const hasBP = moves.has('Baton Pass');
    const hasSetup = [...moves].some(m => SETUP_MOVES.has(m));
    return hasBP && hasSetup;
  });
  const receivers = scored.filter(s => {
    const moves = getMoveSet(s.pokemon);
    if (moves.has('Baton Pass')) return false;
    const usesStoredPower = moves.has('Stored Power') || moves.has('Power Trip');
    const hasSetup = [...moves].some(m => SETUP_MOVES.has(m));
    return usesStoredPower || hasSetup;
  });
  if (passers.length > 0 && receivers.length > 0) {
    for (const p of passers) {
      const label = `Baton Pass setter for ${receivers.map(r => r.pokemon.name).join('/')}`;
      addSynergy(p.pokemon.name, 6, label);
    }
    for (const r of receivers) {
      const moves = getMoveSet(r.pokemon);
      const storedPowerBonus = moves.has('Stored Power') || moves.has('Power Trip') ? 2 : 0;
      const label = `Receives Baton Pass boosts from ${passers.map(p => p.pokemon.name).join('/')}`;
      addSynergy(r.pokemon.name, 4 + storedPowerBonus, label);
    }
    // Pair-lock: bringing the passer without a receiver (or vice versa)
    // defeats the strategy, so link every passer/receiver pair.
    for (const p of passers) {
      for (const r of receivers) linkPartners(p.pokemon.name, r.pokemon.name);
    }
  }

  // --- Redirect + setup sweeper ---
  // Follow Me / Rage Powder user lets a teammate with a setup move boost safely.
  const redirectors = scored.filter(s => {
    const moves = getMoveSet(s.pokemon);
    return moves.has('Follow Me') || moves.has('Rage Powder');
  });
  const setupSweepers = scored.filter(s => {
    const moves = getMoveSet(s.pokemon);
    return [...moves].some(m => SETUP_MOVES.has(m));
  });
  if (redirectors.length > 0 && setupSweepers.length > 0) {
    // Don't double-count the same Pokemon as both roles.
    for (const r of redirectors) {
      const sweeperNames = setupSweepers
        .filter(s => s.pokemon.name !== r.pokemon.name)
        .map(s => s.pokemon.name);
      if (sweeperNames.length === 0) continue;
      addSynergy(r.pokemon.name, 2, `Redirects for ${sweeperNames.join('/')} setup`);
    }
  }

  // --- Trick Room + slow hitter ---
  // TR setter enables slow high-Atk/SpA Pokemon. A "slow hitter" here is
  // base Spe ≤ 70 with base Atk or SpA ≥ 100.
  const trSetters = scored.filter(s => getMoveSet(s.pokemon).has('Trick Room'));
  const slowHitters = scored.filter(s => {
    const stats = s.pokemon.baseStats;
    if (!stats) return false;
    return stats.spe <= 70 && Math.max(stats.atk, stats.spa) >= 100;
  });
  if (trSetters.length > 0 && slowHitters.length > 0) {
    for (const setter of trSetters) {
      addSynergy(setter.pokemon.name, 2, 'Trick Room enables slow hitters');
    }
    for (const hitter of slowHitters) {
      if (trSetters.some(t => t.pokemon.name === hitter.pokemon.name)) continue;
      addSynergy(hitter.pokemon.name, 2, 'Thrives under Trick Room');
    }
  }
}

// ============================================================
// Main recommendation function
// ============================================================
export function getTeamRecommendation(yourTeam, opponentTeam, selectCount = 4, learnedAdjustments = {}, strategy = null, format = null) {
  // Format defaults from selectCount when not explicitly passed: 3 = singles,
  // 4 = doubles. The damage matrix uses this to skip the 0.75x spread modifier
  // in singles (Earthquake, Surf, etc. hit at full power).
  const battleFormat = format || (selectCount === 3 ? 'singles' : 'doubles');
  const strat = normalizeStrategy(strategy);

  // Detect archetype early so triggers can match on it.
  const earlyArchetype = detectArchetype(opponentTeam);

  // Evaluate strategy triggers — these surface forced brings ("if you see
  // Toxapex+Corv, bring Dragonite") that are author-encoded knowledge the
  // damage matrix can't derive. Each match yields one or more forced brings.
  const triggerMatches = evaluateTriggers(strat, opponentTeam, earlyArchetype?.archetype);
  const triggerByPokemon = new Map(); // name -> [{trigger, matchedOn}]
  const forcedBrings = new Set();
  for (const m of triggerMatches) {
    for (const name of m.trigger.bring) {
      forcedBrings.add(name);
      if (!triggerByPokemon.has(name)) triggerByPokemon.set(name, []);
      triggerByPokemon.get(name).push(m);
    }
  }

  // Score and select Pokemon (with breakdown for the UI tooltip).
  const scored = yourTeam.map(pokemon => {
    const breakdown = scorePokemonVsTeamBreakdown(pokemon, opponentTeam);
    return { pokemon, score: breakdown.total, breakdown };
  });

  // Apply bring priors — author-set 0..1 rates ("Floette 95%, Umbreon 0%")
  // become a score bias on top of the damage-matrix score.
  //
  // - prior === 0 is a hard veto ("preview-only scarecrow, never bring").
  //   We push the score deep negative so this mon only appears if a trigger
  //   explicitly forces it (trigger boost can override even a veto).
  // - prior === 1 is a strong "always bring" preference (+8).
  // - Otherwise scales (prior - 0.5) * 16 → ±8 range, large enough to
  //   actually move the needle against typical raw scores (~5-25).
  for (const s of scored) {
    const prior = getBringPrior(strat, s.pokemon.name);
    if (prior === 0) {
      s.score -= 100;
      s.priorBias = -100;
      s.priorVetoed = true;
    } else if (prior !== 0.5) {
      const bias = (prior - 0.5) * 16; // -8..+8
      s.score += bias;
      s.priorBias = bias;
    }
    // Trigger-forced brings get a strong boost so they survive the swap pass
    // — large enough to override a 0% veto when the author explicitly says
    // "if you see X, bring this mon".
    if (forcedBrings.has(s.pokemon.name)) {
      s.score += 120;
      s.triggerForced = true;
    }
  }

  // Apply team synergy bonuses BEFORE sorting so combos like Baton Pass
  // setter + receiver get brought together instead of passed over for
  // generically-strong but un-synergistic picks.
  applyTeamSynergyBonuses(scored);
  scored.sort((a, b) => b.score - a.score);

  // Predict the opponent's likely brings so lead-selection can focus on them
  // and so we can protect likely threats extra hard during coverage swaps.
  const opponentPrediction = predictOpponentSelection(yourTeam, opponentTeam, selectCount, learnedAdjustments);
  const likelyBringNames = new Set(
    (opponentPrediction?.predictions || []).filter(p => p.likely).map(p => p.name)
  );

  // Threat coverage optimization:
  // After initial sort, check if the top selection covers every opponent. We
  // deliberately consider ALL 6 opponents (not just the predicted likely set) —
  // prediction accuracy is too noisy with limited match history to gate on. But
  // when evaluating swap safety ("would this uncover anyone?") we weigh likely
  // brings heavier so we don't sacrifice a real-threat counter for a bench one.
  let selected = scored.slice(0, selectCount);
  let bench = scored.slice(selectCount);

  // Find which opponents each of our Pokemon can hit SE
  function canHitSE(pokemon, opponent) {
    const offTypes = getOffensiveTypes(pokemon);
    return offTypes.some(t => getEffectiveness(t, opponent.types) >= 2);
  }

  // Defensive "counter" — this Pokemon walls the opponent's STAB types
  // (every opp type hits ≤ 0.5x, or immune via type/ability). A hard wall is
  // just as valid a matchup answer as an SE attacker, and we were previously
  // ignoring walls entirely when deciding swap candidates.
  function wallsOpponent(pokemon, opponent) {
    const tags = getAbilityTags(pokemon.ability);
    return opponent.types.every(t => defensiveEffectiveness(t, pokemon.types, tags) <= 0.5);
  }

  function coversOpponent(pokemon, opponent) {
    return canHitSE(pokemon, opponent) || wallsOpponent(pokemon, opponent);
  }

  // Check for uncovered threats across the whole opponent team
  const uncoveredThreats = opponentTeam.filter(opp =>
    !selected.some(s => coversOpponent(s.pokemon, opp))
  );

  // For each uncovered threat, find the best counter on the bench and swap it in
  for (const threat of uncoveredThreats) {
    // Find best bench Pokemon that covers this threat (SE hit OR hard wall).
    // Exclude prior-vetoed mons (0% bring rate) — the user explicitly said
    // "never bring this", so we'd rather leave a threat uncovered than
    // recommend a scarecrow. Trigger-forced mons are exempt from the veto.
    const bestCounter = bench
      .filter(b => coversOpponent(b.pokemon, threat))
      .filter(b => !b.priorVetoed || b.triggerForced)
      .sort((a, b) => b.score - a.score)[0];

    if (bestCounter) {
      // Swap out the lowest-scored selected Pokemon (that isn't the only counter for another threat)
      const worstSelected = [...selected].sort((a, b) => a.score - b.score);
      let swapTarget = null;

      for (const candidate of worstSelected) {
        // Check: if we remove this Pokemon, does every OTHER opponent still have a counter?
        const remainingWithout = selected.filter(s => s !== candidate);
        const remainingPlusBench = [...remainingWithout, bestCounter];
        // Track all newly-uncovered opponents, then partition by likelihood.
        // Swap is fine if it only exposes unlikely brings, but bail if it would
        // leave a predicted-likely threat with no counter.
        const newlyUncovered = opponentTeam.filter(opp =>
          opp.name !== threat.name && !remainingPlusBench.some(s => coversOpponent(s.pokemon, opp))
        );
        const uncoveredLikely = newlyUncovered.filter(o => likelyBringNames.has(o.name));
        if (uncoveredLikely.length === 0) {
          swapTarget = candidate;
          break;
        }
      }

      if (swapTarget) {
        selected = selected.filter(s => s !== swapTarget);
        selected.push(bestCounter);
        bench = bench.filter(b => b !== bestCounter);
        bench.push(swapTarget);
        // Re-sort selected by score (keep order nice)
        selected.sort((a, b) => b.score - a.score);
        bench.sort((a, b) => b.score - a.score);
      }
    }
  }

  // Pair-completion pass: if a selected Pokemon has a synergy partner on the
  // bench but no partner in `selected`, swap in the highest-scored partner.
  // Baton Pass / TR / redirect combos only work when brought as a set — a
  // solo setter or solo receiver is worse than no combo at all.
  const isSelected = (name) => selected.some(s => s.pokemon.name === name);
  // One completion attempt per selected entry (avoids oscillation).
  for (const entry of [...selected]) {
    if (!entry.synergyPartners || entry.synergyPartners.size === 0) continue;
    const hasPartner = [...entry.synergyPartners].some(n => isSelected(n));
    if (hasPartner) continue;
    const benchPartner = bench
      .filter(b => entry.synergyPartners.has(b.pokemon.name))
      .filter(b => !b.priorVetoed || b.triggerForced)
      .sort((a, b) => b.score - a.score)[0];
    if (!benchPartner) continue;
    // Swap out the lowest-scored selected that isn't part of this synergy or
    // the sole counter for a likely-bring opponent.
    const candidates = [...selected]
      .filter(s => s !== entry)
      .sort((a, b) => a.score - b.score);
    let swapTarget = null;
    for (const c of candidates) {
      const remainingPlusBench = [
        ...selected.filter(s => s !== c),
        benchPartner,
      ].filter(s => s !== entry).concat(entry);
      // Don't orphan another synergy pair by removing its only partner.
      if (c.synergyPartners && c.synergyPartners.size > 0) {
        const cPartners = [...c.synergyPartners];
        const cHasOther = cPartners.some(n =>
          remainingPlusBench.some(s => s.pokemon.name === n)
        );
        if (!cHasOther) continue;
      }
      // Don't leave a likely-bring opponent uncovered.
      const newlyUncovered = opponentTeam.filter(opp =>
        !remainingPlusBench.some(s => coversOpponent(s.pokemon, opp))
      );
      const uncoveredLikely = newlyUncovered.filter(o => likelyBringNames.has(o.name));
      if (uncoveredLikely.length === 0) { swapTarget = c; break; }
    }
    if (swapTarget) {
      selected = selected.filter(s => s !== swapTarget);
      selected.push(benchPartner);
      bench = bench.filter(b => b !== benchPartner);
      bench.push(swapTarget);
      selected.sort((a, b) => b.score - a.score);
      bench.sort((a, b) => b.score - a.score);
    }
  }

  // Final veto safety net — if any prior-vetoed mon snuck into selected via
  // any path (initial sort, swap passes, synergy completion), bump it back to
  // bench and pull the next non-vetoed bench mon up. Trigger-forced mons are
  // exempt: an explicit "if you see X bring this" rule overrides the veto.
  const vetoedInSelected = selected.filter(s => s.priorVetoed && !s.triggerForced);
  if (vetoedInSelected.length > 0) {
    for (const v of vetoedInSelected) {
      const replacement = bench.find(b => !b.priorVetoed || b.triggerForced);
      if (!replacement) break; // nothing left that isn't vetoed; leave as-is
      selected = selected.filter(s => s !== v);
      bench = bench.filter(b => b !== replacement);
      selected.push(replacement);
      bench.push(v);
    }
    selected.sort((a, b) => b.score - a.score);
    bench.sort((a, b) => b.score - a.score);
  }

  // Diagnostic — surface the strategy state in console so we can verify what
  // the advisor actually received. Cheap to leave in for now while dialing in.
  if (typeof window !== 'undefined' && window.__ADVISOR_DEBUG !== false) {
    // eslint-disable-next-line no-console
    console.log('[advisor] strategy applied:', {
      bringPriors: strat.bringPriors,
      triggers: strat.triggers.length,
      defaultLead: strat.leadRules.defaultLead,
      scored: scored.map(s => ({
        name: s.pokemon.name,
        score: Math.round(s.score * 10) / 10,
        priorBias: s.priorBias,
        priorVetoed: !!s.priorVetoed,
        triggerForced: !!s.triggerForced,
      })),
      selected: selected.map(s => s.pokemon.name),
    });
  }

  // Generate reasoning for selected Pokemon
  const reasoning = selected.map(({ pokemon, score, breakdown, synergyReasons }) => {
    const reasons = [];
    // Team synergies first — they explain why a "weak on paper" pick was picked.
    if (synergyReasons && synergyReasons.length > 0) {
      for (const r of synergyReasons) reasons.push(r);
    }
    const damagingTypes = getDamagingMoveTypes(pokemon);
    const abilityTags = getAbilityTags(pokemon.ability);

    // SE coverage with move names
    for (const opp of opponentTeam) {
      const seHits = [];
      for (const atkType of pokemon.types) {
        if (getEffectiveness(atkType, opp.types) >= 2) seHits.push(`${atkType} STAB`);
      }
      if (pokemon.moves) {
        for (const move of pokemon.moves) {
          const mt = getMoveType(move);
          if (mt && getEffectiveness(mt, opp.types) >= 2) seHits.push(move);
        }
      }
      if (seHits.length > 0) {
        const moveNames = seHits.filter(s => !s.includes('STAB'));
        const display = moveNames.length > 0 ? moveNames.join(', ') : seHits[0];
        reasons.push(`Hits ${opp.name} SE (${display})`);
      }
    }

    // Walls — ability-aware so Levitate/Water Absorb/etc. count
    const walls = opponentTeam.filter(opp =>
      opp.types.every(t => defensiveEffectiveness(t, pokemon.types, abilityTags) < 1)
    );
    if (walls.length > 0) reasons.push(`Walls ${walls.map(p => p.name).join(', ')}`);

    // Ability-based reasons
    if (pokemon.ability) {
      if (abilityTags.includes('immunity_water')) {
        const waterOpps = opponentTeam.filter(o => o.types.includes('Water'));
        if (waterOpps.length > 0) reasons.push(`${pokemon.ability} absorbs Water from ${waterOpps.map(o=>o.name).join(', ')}`);
      }
      if (abilityTags.includes('immunity_electric')) {
        const elecOpps = opponentTeam.filter(o => o.types.includes('Electric'));
        if (elecOpps.length > 0) reasons.push(`${pokemon.ability} absorbs Electric from ${elecOpps.map(o=>o.name).join(', ')}`);
      }
      if (abilityTags.includes('immunity_fire')) {
        const fireOpps = opponentTeam.filter(o => o.types.includes('Fire'));
        if (fireOpps.length > 0) reasons.push(`${pokemon.ability} absorbs Fire from ${fireOpps.map(o=>o.name).join(', ')}`);
      }
      if (abilityTags.includes('immunity_ground')) {
        const gndOpps = opponentTeam.filter(o => o.types.includes('Ground'));
        if (gndOpps.length > 0) reasons.push(`${pokemon.ability} immune to Ground from ${gndOpps.map(o=>o.name).join(', ')}`);
      }
      if (abilityTags.includes('anti_intimidate') && opponentTeam.some(o =>
        ['Incineroar','Arcanine','Gyarados','Landorus','Krookodile','Luxray'].includes(o.name)
      )) reasons.push(`${pokemon.ability} counters opponent Intimidate`);

      if (abilityTags.includes('anti_priority')) reasons.push(`${pokemon.ability} blocks opponent priority moves`);
      if (pokemon.ability === 'Intimidate') reasons.push('Intimidate weakens physical attackers on entry');
      if (pokemon.ability === 'Regenerator') reasons.push('Regenerator heals on switch — great for pivoting');
    }

    // Move utility
    if (pokemon.moves) {
      const ms = getMoveSet(pokemon);
      if (ms.has('Fake Out')) reasons.push('Fake Out disrupts opponent turn 1');
      if (ms.has('Tailwind')) reasons.push('Tailwind doubles team Speed');
      if (ms.has('Trick Room')) reasons.push('Trick Room reverses Speed');
      if (ms.has('Follow Me') || ms.has('Rage Powder')) reasons.push('Redirects attacks from partner');
      if (ms.has('Taunt')) reasons.push('Taunt blocks opponent setup/TR');
      if (ms.has('Encore')) reasons.push('Encore locks opponent into bad moves');
      if (ms.has('Spore') || ms.has('Sleep Powder')) reasons.push('Can put opponents to sleep');
    }

    if (damagingTypes.length === 0 && pokemon.moves?.length > 0) {
      reasons.push('No damaging moves — coverage from STAB types only');
    }

    return { name: pokemon.name, score: Math.round(score * 10) / 10, reasons, breakdown };
  });

  const benchReasoning = bench.map(({ pokemon, score, breakdown }) => ({
    name: pokemon.name,
    score: Math.round(score * 10) / 10,
    reason: score < 0 ? 'Poor type matchups against this opponent'
      : 'Redundant coverage or less favorable matchups',
    breakdown,
  }));

  // ============================================================
  // Lead pair analysis — evaluate ALL possible lead pairs from selected
  // ============================================================
  // Build the opponent pool the lead will actually face. In order of preference:
  //  1) the opponent's predicted lead(s) — what's most likely on turn 1
  //  2) the opponent's likely brings — what we'll see during the match
  //  3) the full opponent team — fallback for tiny rosters / missing predictions
  // Scoring the lead against this focused pool (instead of all 6) stops us
  // recommending a lead that's great vs the opponent's bench but weak vs who
  // they'll actually send out.
  const oppPredictedLeadNames = opponentPrediction?.predictedLeads?.[0]?.leads || [];
  const oppPredictedLeadPokemon = oppPredictedLeadNames
    .map(n => opponentTeam.find(o => o.name === n))
    .filter(Boolean);
  const oppLikelyBringPokemon = (opponentPrediction?.predictions || [])
    .filter(p => p.likely)
    .map(p => opponentTeam.find(o => o.name === p.name))
    .filter(Boolean);
  const leadOpponentPool = oppPredictedLeadPokemon.length > 0
    ? oppPredictedLeadPokemon
    : (oppLikelyBringPokemon.length > 0 ? oppLikelyBringPokemon : opponentTeam);

  // Blend focused-pool scoring with full-team scoring so a noisy prediction
  // can't push us into a lead that's globally weak. Weighted 60/40 so the
  // prediction influences the pick but can't override a clearly better match.
  const FOCUSED_WEIGHT = 0.6;
  const FULL_WEIGHT = 0.4;

  // Hard penalty for getting OHKO'd by the predicted lead. The regular -5 for
  // a 4x weakness gets diluted by averaging across the full team, which is
  // how Gyarados kept being picked as lead vs Kleavor (4x Rock). On turn 1
  // an OHKO is nearly decisive, so we apply an extra lead-specific penalty
  // that only fires for the predicted opposing lead pool.
  const leadOhkoPenalty = (pokemon) => {
    const tags = getAbilityTags(pokemon.ability);
    let penalty = 0;
    for (const opp of oppPredictedLeadPokemon) {
      for (const oppType of opp.types) {
        const eff = defensiveEffectiveness(oppType, pokemon.types, tags);
        if (eff >= 4) { penalty -= 8; break; }
      }
    }
    return penalty;
  };

  // Strategy-aware lead biases: avoid-lead rules become a hard penalty when
  // the predicted opp lead is in the avoid list; defaultLead gets a soft
  // preference; non-zero priors slightly bias the lead pick too.
  const oppLeadNamesForBias = oppPredictedLeadPokemon.map(p => p.name);
  const stratLeadBias = (pokemon) => {
    let bias = 0;
    const avoid = shouldAvoidLead(strat, pokemon.name, oppLeadNamesForBias);
    if (avoid) bias -= 10;
    if (strat.leadRules.defaultLead === pokemon.name) bias += 1.5;
    const prior = getBringPrior(strat, pokemon.name);
    if (prior !== 0.5) bias += (prior - 0.5) * 2; // -1..+1
    return bias;
  };

  const blendedLeadScore = (pokemon) =>
    scorePokemonVsTeam(pokemon, leadOpponentPool) * FOCUSED_WEIGHT
    + scorePokemonVsTeam(pokemon, opponentTeam) * FULL_WEIGHT
    + leadOhkoPenalty(pokemon)
    + stratLeadBias(pokemon);

  let leadSuggestion = null;
  if (selectCount === 4 && selected.length === 4) {
    const pairs = [];
    for (let i = 0; i < selected.length; i++) {
      for (let j = i + 1; j < selected.length; j++) {
        const { pairScore, reasons } = scoreLeadPair(
          selected[i].pokemon, selected[j].pokemon, leadOpponentPool
        );
        const leadScore1 = blendedLeadScore(selected[i].pokemon);
        const leadScore2 = blendedLeadScore(selected[j].pokemon);
        const totalScore = pairScore + leadScore1 * 0.5 + leadScore2 * 0.5;
        pairs.push({
          leads: [selected[i].pokemon.name, selected[j].pokemon.name],
          back: selected
            .filter((_, idx) => idx !== i && idx !== j)
            .map(s => s.pokemon.name),
          score: Math.round(totalScore * 10) / 10,
          reasons,
        });
      }
    }
    pairs.sort((a, b) => b.score - a.score);
    const best = pairs[0];
    leadSuggestion = {
      leads: best.leads,
      back: best.back,
      reason: best.reasons.length > 0
        ? best.reasons.join('. ')
        : 'Best combined matchup vs their predicted leads',
      allPairs: pairs, // expose all pairs for UI
    };
  } else if (selectCount === 3 && selected.length === 3) {
    // Singles: blend the predicted-lead/likely-brings score with the full-team
    // score. Falls back to the top overall if the blend ties, which protects
    // against cases where the prediction is way off.
    const rescored = selected
      .map(s => ({
        ...s,
        leadScoreBlended: blendedLeadScore(s.pokemon),
      }))
      .sort((a, b) => b.leadScoreBlended - a.leadScoreBlended);
    const lead = rescored[0];
    const poolLabel = oppPredictedLeadPokemon.length > 0
      ? `predicted lead (${oppPredictedLeadPokemon.map(p => p.name).join(', ')})`
      : (oppLikelyBringPokemon.length > 0
        ? `likely brings (${oppLikelyBringPokemon.map(p => p.name).join(', ')})`
        : 'opponent team');
    leadSuggestion = {
      leads: [lead.pokemon.name],
      back: rescored.slice(1).map(s => s.pokemon.name),
      reason: `${lead.pokemon.name} has the best blended matchup (vs ${poolLabel} + full team)`,
    };
  }

  // Opponent bring prediction was computed at the top of this function so the
  // threat-coverage pass could use it — just re-use it here.

  // Resolve actual lead Pokemon objects
  let yourLeadPokemon = leadSuggestion?.leads?.map(n =>
    selected.find(s => s.pokemon.name === n)?.pokemon
      || yourTeam.find(p => p.name === n) // fallback to full team
  ).filter(Boolean) || [];
  // Fallback: if still empty, use first 2 selected
  if (yourLeadPokemon.length < 2 && selected.length >= 2) {
    yourLeadPokemon = selected.slice(0, 2).map(s => s.pokemon);
  }

  let oppLeadNames = opponentPrediction?.predictedLeads?.[0]?.leads || [];
  let oppLeadPokemon = oppLeadNames.map(n => opponentTeam.find(p => p.name === n)).filter(Boolean);
  // Fallback: if no leads predicted, use the top 2 predicted brings
  if (oppLeadPokemon.length < 2) {
    const topBrings = (opponentPrediction?.predictions || []).filter(p => p.likely).slice(0, 2);
    oppLeadPokemon = topBrings.map(p => opponentTeam.find(o => o.name === p.name)).filter(Boolean);
    oppLeadNames = oppLeadPokemon.map(p => p.name);
  }

  // 1. Archetype detection
  const archetypeAnalysis = detectArchetype(opponentTeam);

  // 2. Damage estimates
  // Singles: all 6 vs all 6 (full roster since any can come in)
  // Doubles: your bring-4 vs opponent predicted bring-4
  const yourSelectedPokemon = selected.map(s => s.pokemon);
  const oppPredictedBrings = (opponentPrediction?.predictions || []).filter(p => p.likely).map(p => opponentTeam.find(o => o.name === p.name)).filter(Boolean);
  // Always calc all 6 vs all 6 — user wants full coverage
  const damageYours = generateDamageMatrix(yourTeam, opponentTeam, { format: battleFormat });
  const damageTheirs = generateDamageMatrix(opponentTeam, yourTeam, { format: battleFormat });
  const damageEstimates = { yourAttacking: damageYours, theirAttacking: damageTheirs, keyFindings: [] };

  // Key findings — OHKOs and 2HKOs
  for (const d of damageYours) {
    if (d.hko === 'OHKO') damageEstimates.keyFindings.push(`Your ${d.attacker} OHKOs ${d.defender} with ${d.move} (${d.minPct}-${d.maxPct}%)`);
    else if (d.hko === '2HKO') damageEstimates.keyFindings.push(`Your ${d.attacker} 2HKOs ${d.defender} with ${d.move} (${d.minPct}-${d.maxPct}%)`);
  }
  for (const d of damageTheirs) {
    if (d.hko === 'OHKO') damageEstimates.keyFindings.push(`Their ${d.attacker} OHKOs your ${d.defender} with ${d.move} (${d.minPct}-${d.maxPct}%)`);
    else if (d.hko === '2HKO') damageEstimates.keyFindings.push(`Their ${d.attacker} 2HKOs your ${d.defender} with ${d.move} (${d.minPct}-${d.maxPct}%)`);
  }

  // 3. Win conditions
  const winConditions = identifyWinConditions(yourSelectedPokemon, opponentTeam, oppPredictedBrings);

  // 4. Battle plan — bring / lead / why / watch-outs (replaces old decision tree)
  const yourSelectedPokemonForPlan = selected.map(s => s.pokemon);
  const battlePlan = generateBattlePlan(
    yourSelectedPokemonForPlan, yourLeadPokemon, opponentTeam,
    opponentPrediction, damageEstimates, selectCount,
    { strategy: strat, triggerByPokemon, triggerMatches }
  );
  // `decisionTree` is kept as a thin alias so the battlefield UI's move-hint
  // lookup (`dt.turns[0].scenarios[0].response[*]`) still works.
  const decisionTree = { turns: battlePlan.turns };

  // 5. Strategic commentary (enhanced with archetype)
  const commentary = generateCommentary(yourLeadPokemon, opponentPrediction, yourTeam, opponentTeam, selectCount);

  return {
    selected: reasoning, benched: benchReasoning, leadSuggestion, opponentPrediction,
    commentary, archetypeAnalysis, damageEstimates, winConditions, decisionTree, battlePlan,
  };
}

/**
 * Recompute just the game plan (decision tree + commentary) with confirmed opponent leads.
 * Called live when user marks actual opponent leads in the UI.
 */
export function recomputeGamePlan(yourLeads, confirmedOppLeads, opponentTeam, yourTeam, selectCount, strategy = null, format = null) {
  const strat = normalizeStrategy(strategy);
  const battleFormat = format || (selectCount === 3 ? 'singles' : 'doubles');
  // Build a richer matrix: leads vs the full opponent team (so the plan can
  // still reason about back-line threats), and yourTeam vs leads.
  const oppForMatrix = (opponentTeam && opponentTeam.length > 0) ? opponentTeam : confirmedOppLeads;
  const yourForMatrix = (yourTeam && yourTeam.length > 0) ? yourTeam : yourLeads;
  const damageYours = generateDamageMatrix(yourForMatrix, oppForMatrix, { format: battleFormat });
  const damageTheirs = generateDamageMatrix(oppForMatrix, yourForMatrix, { format: battleFormat });
  const damageEstimates = { yourAttacking: damageYours, theirAttacking: damageTheirs, keyFindings: [] };

  const fakeOppPrediction = {
    predictions: oppForMatrix.map(p => ({
      name: p.name, types: p.types,
      likely: confirmedOppLeads.some(c => c.name === p.name),
    })),
    predictedLeads: [{ leads: confirmedOppLeads.map(p => p.name) }],
  };

  // Re-evaluate triggers vs the current opp set so trigger reasons appear
  // in the live recompute as well.
  const archetype = detectArchetype(oppForMatrix);
  const triggerMatches = evaluateTriggers(strat, oppForMatrix, archetype?.archetype);
  const triggerByPokemon = new Map();
  for (const m of triggerMatches) {
    for (const name of m.trigger.bring) {
      if (!triggerByPokemon.has(name)) triggerByPokemon.set(name, []);
      triggerByPokemon.get(name).push(m);
    }
  }

  const battlePlan = generateBattlePlan(
    yourLeads, yourLeads, oppForMatrix, fakeOppPrediction, damageEstimates, selectCount,
    { strategy: strat, triggerByPokemon, triggerMatches }
  );
  const decisionTree = { turns: battlePlan.turns };

  const commentary = generateCommentary(yourLeads, fakeOppPrediction, yourForMatrix, oppForMatrix, selectCount);

  return { decisionTree, battlePlan, commentary };
}

// ============================================================
// Archetype Detection
// ============================================================
function detectArchetype(opponentTeam) {
  const names = opponentTeam.map(p => p.name);
  const types = opponentTeam.flatMap(p => p.types);
  const scores = {};

  // Rain
  const rainSetters = names.filter(n => ['Pelipper','Politoed'].includes(n));
  const rainAbusers = names.filter(n => ['Barraskewda','Basculegion','Kingdra','Ludicolo'].includes(n));
  scores.Rain = rainSetters.length * 3 + rainAbusers.length * 2 + types.filter(t => t === 'Water').length * 0.5;

  // Sun
  const sunSetters = names.filter(n => ['Torkoal','Ninetales'].includes(n));
  const sunAbusers = names.filter(n => ['Venusaur','Charizard','Victreebel','Meganium'].includes(n));
  scores.Sun = sunSetters.length * 3 + sunAbusers.length * 2 + types.filter(t => t === 'Fire').length * 0.3;

  // Sand
  const sandSetters = names.filter(n => ['Tyranitar','Hippowdon'].includes(n));
  const sandAbusers = names.filter(n => ['Excadrill','Garchomp'].includes(n));
  scores.Sand = sandSetters.length * 3 + sandAbusers.length * 2;

  // Snow
  const snowSetters = names.filter(n => ['Abomasnow','Ninetales-Alola','Froslass'].includes(n));
  scores.Snow = snowSetters.length * 3 + types.filter(t => t === 'Ice').length * 0.5;

  // Trick Room
  const trSetters = names.filter(n => KNOWN_ROLES.trSetters.includes(n));
  scores['Trick Room'] = trSetters.length * 3;

  // Tailwind HO
  const twSetters = names.filter(n => KNOWN_ROLES.tailwind.includes(n));
  scores['Tailwind HO'] = twSetters.length * 3;

  // Balance (fallback)
  scores.Balance = 2;

  // Find best
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const best = sorted[0];
  const second = sorted[1];
  const isHybrid = second && second[1] >= best[1] * 0.7 && second[1] > 3;

  const archetype = isHybrid ? `${best[0]} / ${second[0]}` : best[0];

  const counterAdvice = {
    Rain: ['Override weather with Sun/Sand', 'Target the rain setter (Pelipper/Politoed)', 'Electric and Grass types excel', 'Bring Pokemon with Water Absorb or Dry Skin'],
    Sun: ['Override weather with Rain/Sand', 'Target the sun setter (Torkoal)', 'Rock types resist Fire and hit back hard', 'Flash Fire absorbs boosted Fire attacks'],
    Sand: ['Override weather', 'Fighting types threaten Tyranitar + Excadrill', 'Water types resist Rock Slide and threaten Ground types'],
    Snow: ['Fire types melt the snow team', 'Fighting and Steel resist Ice', 'Override weather to remove Blizzard accuracy'],
    'Trick Room': ['Fake Out or Taunt the TR setter', 'If TR goes up, stall 5 turns with Protect', 'Bring your own fast threats to pressure before TR', 'Your own TR setter can reverse it back'],
    'Tailwind HO': ['Your own speed control (TR or Tailwind) matches them', 'Priority moves bypass Tailwind advantage', 'Bulky Pokemon survive the offense and outlast 4 turns'],
    Balance: ['No dominant strategy to exploit — play your own game plan', 'Focus on type matchups and coverage'],
  };

  const signals = [];
  if (rainSetters.length) signals.push(`Rain setter: ${rainSetters.join(', ')}`);
  if (sunSetters.length) signals.push(`Sun setter: ${sunSetters.join(', ')}`);
  if (sandSetters.length) signals.push(`Sand setter: ${sandSetters.join(', ')}`);
  if (snowSetters.length) signals.push(`Snow setter: ${snowSetters.join(', ')}`);
  if (trSetters.length) signals.push(`TR setter: ${trSetters.join(', ')}`);
  if (twSetters.length) signals.push(`Tailwind: ${twSetters.join(', ')}`);

  const advice = counterAdvice[best[0]] || counterAdvice.Balance;

  return { archetype, confidence: best[1] > 5 ? 'high' : best[1] > 3 ? 'medium' : 'low', signals, counterAdvice: advice };
}

// ============================================================
// Win Condition Identification
// ============================================================
function identifyWinConditions(yourTeam, opponentTeam, oppLikelyBrings) {
  const yourWinCons = [];
  const oppWinCons = [];
  const mustKill = [];
  const mustProtect = [];

  // Your sweepers: high offensive stats or offensive abilities/moves
  for (const p of yourTeam) {
    const base = p.megaStats || p.baseStats;
    if (!base) continue;
    const isOffensive = base.atk >= 120 || base.spa >= 120;
    const hasSweepAbility = ['Moxie','Supreme Overlord','Speed Boost'].includes(p.ability);
    const hasSetupMove = (p.moves || []).some(m => ['Swords Dance','Nasty Plot','Dragon Dance','Calm Mind','Quiver Dance','Shell Smash','Belly Drum'].includes(m));

    if (isOffensive || hasSweepAbility || hasSetupMove) {
      const offTypes = getOffensiveTypes(p);
      const blockers = opponentTeam.filter(opp =>
        offTypes.every(t => getEffectiveness(t, opp.types) <= 1)
      );

      let desc;
      if (p.ability === 'Supreme Overlord') {
        desc = `${p.name} sweeps endgame with Supreme Overlord — gets stronger as teammates fall`;
      } else if (hasSetupMove) {
        const setupMove = (p.moves || []).find(m => ['Swords Dance','Nasty Plot','Dragon Dance','Calm Mind','Quiver Dance','Shell Smash'].includes(m));
        desc = `${p.name} sweeps after ${setupMove} setup`;
      } else {
        desc = `${p.name} threatens with high offensive power`;
      }

      if (blockers.length > 0) {
        desc += ` once ${blockers.map(b => b.name).join(', ')} ${blockers.length === 1 ? 'is' : 'are'} removed`;
        blockers.forEach(b => { if (!mustKill.includes(b.name)) mustKill.push(b.name); });
      }

      yourWinCons.push({ pokemon: p.name, description: desc, blockers: blockers.map(b => b.name), priority: hasSweepAbility || hasSetupMove ? 'primary' : 'secondary' });
      mustProtect.push(p.name);
    }
  }

  // Opponent threats
  const oppPool = oppLikelyBrings.length > 0 ? oppLikelyBrings : opponentTeam;
  for (const opp of oppPool) {
    const seAgainst = yourTeam.filter(yours =>
      opp.types.some(t => getEffectiveness(t, yours.types) >= 2)
    );
    const wallsYou = yourTeam.filter(yours =>
      yours.types.every(t => getEffectiveness(t, opp.types) <= 1)
    );

    if (seAgainst.length >= 3) {
      oppWinCons.push({
        pokemon: opp.name,
        description: `${opp.name} threatens ${seAgainst.length} of your team SE — high priority target`,
        priority: 'primary',
      });
    } else if (wallsYou.length >= 3) {
      oppWinCons.push({
        pokemon: opp.name,
        description: `${opp.name} walls ${wallsYou.length} of your team — must be handled with coverage moves`,
        priority: 'primary',
      });
    }
  }

  // Check for known endgame threats
  for (const opp of opponentTeam) {
    if (opp.name === 'Kingambit') {
      oppWinCons.push({ pokemon: 'Kingambit', description: 'Kingambit gets +50% power via Supreme Overlord endgame — KO it early or save a Fighting type', priority: 'primary' });
    }
  }

  return { yourWinConditions: yourWinCons, opponentWinConditions: oppWinCons, mustKill, mustProtect };
}

// ============================================================
// Ability-aware helpers
// ============================================================

// Abilities that absorb/immune certain move types (defender ability → immune type)
const ABILITY_IMMUNITIES = {
  'Water Absorb': 'Water', 'Dry Skin': 'Water', 'Storm Drain': 'Water',
  'Volt Absorb': 'Electric', 'Lightning Rod': 'Electric', 'Motor Drive': 'Electric',
  'Flash Fire': 'Fire',
  'Sap Sipper': 'Grass',
  'Earth Eater': 'Ground', 'Levitate': 'Ground',
};

// Abilities that reduce first-hit damage at full HP
const FULL_HP_SHIELDS = ['Multiscale', 'Shadow Shield', 'Ice Scales'];
// Abilities that survive one KO
const DISGUISE_ABILITIES = ['Disguise', 'Ice Face'];
// Abilities that prevent OHKO from full HP
const STURDY_ABILITIES = ['Sturdy'];

/**
 * Check if an opponent has an ability that immunises a move type.
 * Returns the immune type string or null.
 */
function getAbilityImmunity(pokemon) {
  return ABILITY_IMMUNITIES[pokemon.ability] || null;
}

/**
 * Check if a move would be absorbed/immune due to defender ability.
 */
function isAbilityImmune(moveType, defender) {
  const immuneType = ABILITY_IMMUNITIES[defender.ability];
  return immuneType === moveType;
}

/**
 * Analyse mega evolution timing for a Pokemon.
 * Returns { shouldDelay, reason, megaBenefit } or null if not a mega candidate.
 */
function analyseMegaTiming(pokemon, oppLeads, damageEstimates) {
  if (!canMegaEvolve(pokemon)) return null;
  // Must actually be holding a Mega Stone — otherwise mega evolution isn't possible.
  if (!isMegaStone(pokemon.item)) return null;

  const baseAbility = pokemon.ability || '';
  const baseName = pokemon.name;
  const megaStats = getMegaStats(baseName);
  const baseStats = pokemon.baseStats;
  if (!megaStats || !baseStats) return null;

  const reasons = { delay: [], immediate: [] };

  // --- Reasons to DELAY mega evolution ---

  // Multiscale / Shadow Shield: halves damage at full HP — mega evolving loses this
  if (FULL_HP_SHIELDS.includes(baseAbility)) {
    // Check if opponents threaten us — the shield matters more if we're under threat
    const theirHits = (damageEstimates.theirAttacking || []).filter(d => d.defender === baseName);
    const worstHit = theirHits.sort((a, b) => b.maxPct - a.maxPct)[0];
    if (worstHit) {
      reasons.delay.push(`${baseAbility} halves damage at full HP — absorb the first hit before mega evolving`);
      if (worstHit.maxPct >= 50) {
        reasons.delay.push(`${worstHit.attacker}'s ${worstHit.move} does ${worstHit.minPct}-${worstHit.maxPct}% — with ${baseAbility} you survive; without it you might not`);
      }
    } else {
      reasons.delay.push(`${baseAbility} halves first-hit damage — delay mega unless you need the offensive boost now`);
    }
  }

  // Intimidate: you get the -1 Atk debuff on switch-in; mega evolving doesn't re-trigger it
  if (baseAbility === 'Intimidate') {
    const oppPhysical = oppLeads.filter(opp => {
      const stats = opp.baseStats;
      return stats && stats.atk > stats.spa;
    });
    if (oppPhysical.length > 0) {
      reasons.delay.push(`Intimidate already lowered ${oppPhysical.map(p => p.name).join('/')}'s Attack — the value is banked`);
    }
  }

  // --- Reasons to MEGA EVOLVE IMMEDIATELY ---

  // Speed gain: if mega form outspeeds key threats that base form doesn't
  const baseSpe = baseStats.spe;
  const megaSpe = megaStats.spe;
  if (megaSpe > baseSpe) {
    for (const opp of oppLeads) {
      const oppStats = getEffectiveStats(opp);
      if (oppStats) {
        const baseSpeed = getEffectiveSpeed(pokemon);
        const megaPokemon = { ...pokemon, baseStats: megaStats, megaStats: null };
        const megaSpeed = getEffectiveSpeed(megaPokemon);
        if (baseSpeed < oppStats.spe && megaSpeed >= oppStats.spe) {
          reasons.immediate.push(`Mega form outspeeds ${opp.name} (${megaSpe} vs ${opp.baseStats?.spe || '?'} base) — base form doesn't`);
        }
      }
    }
  }

  // Offensive power: if mega form secures a KO that base form misses
  const ourHits = (damageEstimates.yourAttacking || []).filter(d => d.attacker === baseName);
  for (const hit of ourHits) {
    if (hit.hko !== 'OHKO' && hit.maxPct >= 70) {
      // The mega stat boost might push this into OHKO range
      const atkGain = Math.max(megaStats.atk - baseStats.atk, megaStats.spa - baseStats.spa);
      if (atkGain >= 20) {
        reasons.immediate.push(`Mega's +${atkGain} offense may turn the ${hit.minPct}-${hit.maxPct}% on ${hit.defender} into a KO`);
        break;
      }
    }
  }

  // Type change: if mega type is defensively better for this matchup
  const megaTypes = getMegaTypes(baseName, pokemon.item);
  if (megaTypes) {
    const baseTypes = pokemon.types || [];
    // Check if any opponent has SE against base but not mega
    for (const opp of oppLeads) {
      for (const oppType of (opp.types || [])) {
        const vsBase = getEffectiveness(oppType, baseTypes);
        const vsMega = getEffectiveness(oppType, megaTypes);
        if (vsBase >= 2 && vsMega <= 1) {
          reasons.immediate.push(`Mega type change removes ${oppType} weakness from ${opp.name}`);
        }
      }
    }
  }

  // Mega form has a critical ability (e.g., Snow Warning on Froslass)
  // We can't know the mega ability from data, but we can flag weather megas
  if (baseName === 'Froslass') {
    reasons.immediate.push('Mega Froslass sets Snow via Snow Warning — 100% accurate Blizzard');
  }

  // Decide
  const shouldDelay = reasons.delay.length > 0 && reasons.immediate.length === 0;
  const shouldMegaNow = reasons.immediate.length > 0 && reasons.delay.length === 0;

  return {
    shouldDelay,
    shouldMegaNow,
    delayReasons: reasons.delay,
    immediateReasons: reasons.immediate,
    isConflicted: reasons.delay.length > 0 && reasons.immediate.length > 0,
  };
}


// ============================================================
// Battle Plan — Bring / Lead / Why / Watch-outs (rules engine)
// ============================================================
// Replaces the old turn-by-turn decision tree with concrete, damage-driven
// brings, lead picks, and severity-coded watch-outs. The old tree leaned on
// canned scenario templates ("Favorable Matchup", "Trick Room + Fake Out")
// that often contradicted the actual matchup data — this version reads from
// the damage matrix and ability data to produce specific, actionable advice.

const ABILITY_TYPE_IMMUNITY = {
  'Levitate': 'Ground',
  'Flash Fire': 'Fire',
  'Water Absorb': 'Water', 'Dry Skin': 'Water', 'Storm Drain': 'Water',
  'Volt Absorb': 'Electric', 'Lightning Rod': 'Electric', 'Motor Drive': 'Electric',
  'Sap Sipper': 'Grass',
  'Earth Eater': 'Ground',
};

// Abilities that completely block first-hit damage or heavily reduce it.
// (Existing FULL_HP_SHIELDS array up-file is broader — used for mega timing.)
const FIRST_HIT_HALVERS = new Set(['Multiscale', 'Shadow Shield']);
const DISGUISE_LIKE = new Set(['Disguise', 'Ice Face']);
const STURDY_LIKE = new Set(['Sturdy']);

// Abilities the opponent uses that should change how we attack.
const ABILITY_DANGERS = {
  'Speed Boost': { sev: 'critical', tip: 'gains +1 Speed every turn — KO turn 1 or get swept' },
  'Magic Bounce': { sev: 'warn', tip: 'reflects status moves — do not Taunt / WoW / T-Wave' },
  'Magic Guard': { sev: 'info', tip: 'ignores chip damage (Sand, burn, Sash bypass) — needs a clean KO' },
  'Prankster': { sev: 'warn', tip: 'priority status moves — expect first-turn Tailwind / Taunt / T-Wave' },
  'Unaware': { sev: 'warn', tip: 'ignores stat boosts — do not bother setting up' },
  'Mold Breaker': { sev: 'warn', tip: 'ignores your defensive abilities (Levitate, Multiscale, etc.)' },
  'Teravolt': { sev: 'warn', tip: 'ignores your defensive abilities' },
  'Turboblaze': { sev: 'warn', tip: 'ignores your defensive abilities' },
  'Regenerator': { sev: 'info', tip: 'heals 33% on switch — predict the pivot, punish the switch-in' },
  'Toxic Debris': { sev: 'warn', tip: 'physical hits set Toxic Spikes — pivot grounded mons carefully' },
  'Defiant': { sev: 'warn', tip: 'gains +2 Atk from Intimidate — do not click Intimidate-mons in' },
  'Competitive': { sev: 'warn', tip: 'gains +2 SpA from Intimidate — do not click Intimidate-mons in' },
  'Mirror Armor': { sev: 'warn', tip: 'reflects stat drops — Intimidate hits you instead' },
  'Justified': { sev: 'info', tip: 'gains +1 Atk from Dark moves — avoid clicking Dark on it' },
  'Rattled': { sev: 'info', tip: 'gains +1 Speed from Bug/Dark/Ghost — avoid those types' },
  'Anger Point': { sev: 'warn', tip: 'maxes Atk on a crit — do not chance high-crit moves' },
};

const PIVOT_MOVES = new Set(['U-turn', 'Volt Switch', 'Flip Turn', 'Parting Shot', 'Teleport', 'Baton Pass']);

const SETUP_MOVES_LIST = new Set([
  'Swords Dance', 'Nasty Plot', 'Dragon Dance', 'Quiver Dance', 'Calm Mind',
  'Bulk Up', 'Iron Defense', 'Agility', 'Rock Polish', 'Shell Smash',
  'Shift Gear', 'Clangorous Soul', 'Cosmic Power', 'Growth',
]);

// `KNOWN_ROLES` is declared further down in the file — wrap these in lazy
// getters so we don't hit a temporal-dead-zone at module load time.
const isTrSetter = (name) => KNOWN_ROLES.trSetters.includes(name);
const isTailwindUser = (name) => (KNOWN_ROLES.tailwind || []).includes(name);

function moveCat(moveName) {
  return MOVE_DATA?.[moveName]?.cat || null;
}

function hkoRank(hko) {
  if (hko === 'OHKO') return 4;
  if (hko === '2HKO') return 3;
  if (hko === '3HKO') return 2;
  if (hko === '4HKO') return 1;
  return 0;
}

function bestKOAgainst(matrix, attackerName, defenderName) {
  if (!matrix) return null;
  let best = null;
  for (const d of matrix) {
    if (d.attacker !== attackerName || d.defender !== defenderName) continue;
    if (!best
      || hkoRank(d.hko) > hkoRank(best.hko)
      || (hkoRank(d.hko) === hkoRank(best.hko) && d.maxPct > best.maxPct)
    ) best = d;
  }
  return best;
}

function isFasterThan(p1, p2) {
  const s1 = getEffectiveSpeed(p1);
  const s2 = getEffectiveSpeed(p2);
  if (s1 == null || s2 == null) return null;
  if (s1 === s2) return 'tie';
  return s1 > s2;
}

// ----- Per-Pokemon "why bring" reasoning -----
function buildBringEntry(pokemon, opponentTeam, oppLikelyBrings, damageEstimates, learnedRole, ctx = {}) {
  const reasons = [];
  const triggerReasons = [];
  const scriptLines = [];
  const oneVOneLines = [];
  const tags = getAbilityTags(pokemon.ability);
  const yourMatrix = damageEstimates?.yourAttacking || [];
  const theirMatrix = damageEstimates?.theirAttacking || [];

  // 0. Strategy triggers — author-encoded "if you see X, bring Y" rules
  // get top billing in the why list because they explain *why* this mon
  // is being recommended in the first place.
  const matches = ctx.triggerByPokemon?.get(pokemon.name) || [];
  for (const m of matches.slice(0, 3)) {
    const rsn = m.trigger.reason || `Triggered by ${m.matchedOn}`;
    triggerReasons.push(`[Trigger: ${m.matchedOn}] ${rsn}`);
  }

  // 0a. Setup scripts — author script lines that fire when relevant opp is likely.
  if (ctx.strategy) {
    const oppNames = (oppLikelyBrings.length > 0 ? oppLikelyBrings : opponentTeam).map(o => o.name);
    const scripts = findSetupScripts(ctx.strategy, pokemon.name, oppNames);
    for (const s of scripts.slice(0, 3)) scriptLines.push({ vs: s.vs.join(' / '), script: s.script });
    const oneVs = findKeyOneVOnes(ctx.strategy, pokemon.name, oppNames);
    for (const o of oneVs.slice(0, 4)) oneVOneLines.push({ vs: o.vs, note: o.note });
  }

  // 1. Concrete OHKOs/2HKOs against likely brings (highest priority).
  const koLines = [];
  for (const opp of oppLikelyBrings.length > 0 ? oppLikelyBrings : opponentTeam) {
    const ko = bestKOAgainst(yourMatrix, pokemon.name, opp.name);
    if (!ko) continue;
    if (ko.hko === 'OHKO') koLines.push({ rank: 4, text: `OHKOs ${opp.name} with ${ko.move} (${ko.minPct}-${ko.maxPct}%)` });
    else if (ko.hko === '2HKO') koLines.push({ rank: 3, text: `2HKOs ${opp.name} with ${ko.move} (${ko.minPct}-${ko.maxPct}%)` });
  }
  koLines.sort((a, b) => b.rank - a.rank);
  for (const k of koLines.slice(0, 3)) reasons.push(k.text);

  // 2. Walls — ability-aware. Opponent's STAB types all hit ≤ 0.5x.
  const walled = [];
  for (const opp of oppLikelyBrings.length > 0 ? oppLikelyBrings : opponentTeam) {
    const everyTypeResisted = opp.types.every(t => defensiveEffectiveness(t, pokemon.types, tags) <= 0.5);
    if (everyTypeResisted) walled.push(opp.name);
  }
  if (walled.length > 0) reasons.push(`Walls ${walled.slice(0, 3).join(', ')}`);

  // 3. Ability immunity vs threats.
  const immuneType = ABILITY_TYPE_IMMUNITY[pokemon.ability];
  if (immuneType) {
    const targets = opponentTeam.filter(o => o.types.includes(immuneType)
      || (o.moves || []).some(m => MOVE_TYPES[m] === immuneType));
    if (targets.length > 0) {
      reasons.push(`${pokemon.ability} absorbs ${immuneType} from ${targets.map(o => o.name).slice(0, 3).join(', ')}`);
    }
  }

  // 4. Utility moves that matter against this team.
  const moves = new Set(pokemon.moves || []);
  if (moves.has('Fake Out')) reasons.push('Fake Out flinch buys a free turn (great vs setup leads)');
  if (moves.has('Taunt') && opponentTeam.some(o => isTrSetter(o.name))) {
    reasons.push('Taunt blocks their Trick Room setter');
  }
  if (moves.has('Trick Room')) reasons.push('Trick Room flips Speed for our slow hitters');
  if (moves.has('Tailwind')) reasons.push('Tailwind doubles our Speed for 4 turns');
  if (moves.has('Follow Me') || moves.has('Rage Powder')) reasons.push('Redirects attacks off our setup partner');
  if (moves.has('Encore') && opponentTeam.some(o => (o.moves || []).some(m => SETUP_MOVES_LIST.has(m) || PIVOT_MOVES.has(m)))) {
    reasons.push('Encore locks them into a setup or pivot move');
  }

  // 5. Priority moves vs offensive opponents.
  const priorityMove = (pokemon.moves || []).find(m => (MOVE_DATA?.[m]?.priority || 0) > 0 && moveCat(m) !== 'Status');
  if (priorityMove) reasons.push(`${priorityMove} priority — revenge-kills weakened threats`);

  // 6. Speed control / weather synergy (defer to commentary if redundant).
  if (tags.includes('weather_sun') || tags.includes('weather_rain') || tags.includes('weather_sand') || tags.includes('weather_snow')) {
    const weather = pokemon.ability;
    reasons.push(`${weather} sets weather — boosts our offense`);
  }

  // 7. What this Pokemon CAN'T do — flag matchups it loses.
  const losesTo = [];
  for (const opp of oppLikelyBrings.length > 0 ? oppLikelyBrings : opponentTeam) {
    const incoming = bestKOAgainst(theirMatrix, opp.name, pokemon.name);
    if (incoming?.hko === 'OHKO') {
      const fasterCheck = isFasterThan(opp, pokemon);
      if (fasterCheck === true) losesTo.push(`${opp.name} OHKOs and outspeeds`);
      else if (fasterCheck === 'tie') losesTo.push(`${opp.name} OHKOs (speed tie)`);
    }
  }

  // Role label — use stored role or infer from context.
  let role = learnedRole || pokemon.role || '';
  if (!role) {
    const stats = pokemon.baseStats;
    if (moves.has('Trick Room') || moves.has('Tailwind') || moves.has('Follow Me') || moves.has('Rage Powder')) role = 'Support';
    else if (stats && Math.max(stats.atk, stats.spa) >= 120) role = 'Wallbreaker';
    else if (stats && stats.spe >= 110) role = 'Speed control';
    else role = 'Utility';
  }

  // Trigger-driven reasons go first, followed by data-driven reasons.
  const why = [...triggerReasons, ...reasons].slice(0, 6);

  return {
    name: pokemon.name,
    role,
    why,
    losesTo: losesTo.slice(0, 2),
    scripts: scriptLines,
    oneVOnes: oneVOneLines,
    triggered: triggerReasons.length > 0,
  };
}

// ----- Lead pick + reasoning + avoid -----
function buildLeadEntry(yourLeads, yourSelected, opponentTeam, opponentPrediction, damageEstimates, selectCount, ctx = {}) {
  if (!yourLeads || yourLeads.length === 0) return null;
  const isDoubles = selectCount === 4;
  const yourMatrix = damageEstimates?.yourAttacking || [];
  const theirMatrix = damageEstimates?.theirAttacking || [];

  const oppLeadNames = opponentPrediction?.predictedLeads?.[0]?.leads || [];
  const oppLeads = oppLeadNames.map(n => opponentTeam.find(o => o.name === n)).filter(Boolean);
  const oppLikelyNames = (opponentPrediction?.predictions || []).filter(p => p.likely).map(p => p.name);
  const oppLikely = oppLikelyNames.map(n => opponentTeam.find(o => o.name === n)).filter(Boolean);

  const why = [];
  const avoid = [];

  // Strategy avoid-lead rules — author-encoded "don't lead X into Y" rules
  // get priority placement at the top of the avoid list.
  if (ctx.strategy) {
    for (const lead of yourLeads) {
      const hit = shouldAvoidLead(ctx.strategy, lead.name, oppLeadNames);
      if (hit) avoid.push(`Strategy: don't lead ${hit.lead} into ${hit.into} — ${hit.reason}`);
    }
    // Also flag if any of our other selected mons would be better avoiders.
    const benchedSelected = yourSelected.filter(s => !yourLeads.some(l => l.name === s.name));
    for (const back of benchedSelected) {
      const hit = shouldAvoidLead(ctx.strategy, back.name, oppLeadNames);
      if (hit) avoid.push(`Strategy: ${back.name} should not lead vs ${hit.into}`);
    }
    // Default lead override hint.
    if (ctx.strategy.leadRules?.defaultLead && yourLeads.every(l => l.name !== ctx.strategy.leadRules.defaultLead)) {
      const def = ctx.strategy.leadRules.defaultLead;
      if (yourSelected.some(s => s.name === def)) {
        why.push(`Strategy default lead is ${def} — overridden here for matchup reasons`);
      }
    }
  }

  // Reasons to lead this pair: each lead's pressure on the predicted opp leads.
  for (const lead of yourLeads) {
    const tags = getAbilityTags(lead.ability);

    // Best KO vs predicted leads.
    const koTargets = [];
    for (const opp of oppLeads.length > 0 ? oppLeads : oppLikely) {
      const ko = bestKOAgainst(yourMatrix, lead.name, opp.name);
      if (ko?.hko === 'OHKO') koTargets.push(`OHKO ${opp.name} with ${ko.move}`);
      else if (ko?.hko === '2HKO' && ko.maxPct >= 80) koTargets.push(`2HKO ${opp.name} with ${ko.move}`);
    }
    if (koTargets.length > 0) why.push(`${lead.name}: ${koTargets[0]}`);

    // Speed advantage vs predicted leads.
    for (const opp of oppLeads) {
      const cmp = isFasterThan(lead, opp);
      if (cmp === true) {
        const ko = bestKOAgainst(yourMatrix, lead.name, opp.name);
        if (ko && ko.hko !== 'none') why.push(`${lead.name}: outspeeds ${opp.name} and ${ko.hko === 'OHKO' ? 'OHKOs' : 'pressures'} it`);
      }
    }

    // Wall vs predicted leads.
    const walls = oppLeads.filter(o => o.types.every(t => defensiveEffectiveness(t, lead.types, tags) <= 0.5));
    if (walls.length > 0) why.push(`${lead.name}: walls ${walls.map(w => w.name).join(', ')} on entry`);

    // Setup leads (Intimidate, Fake Out, Tailwind, TR, weather).
    if (lead.ability === 'Intimidate') why.push(`${lead.name}: Intimidate softens their physical lead`);
    if (lead.ability === 'Drought') why.push(`${lead.name}: Drought sets Sun for our offense`);
    if (lead.ability === 'Drizzle') why.push(`${lead.name}: Drizzle sets Rain for our offense`);
    if (lead.ability === 'Sand Stream') why.push(`${lead.name}: Sand Stream sets Sand`);
    if ((lead.moves || []).includes('Fake Out')) why.push(`${lead.name}: Fake Out flinch into partner setup`);
    if ((lead.moves || []).includes('Trick Room')) why.push(`${lead.name}: Trick Room flips Speed turn 1`);
    if ((lead.moves || []).includes('Tailwind') && isDoubles) why.push(`${lead.name}: Tailwind doubles team Speed`);
  }

  // Avoid lines: leads on the bench/selected that get destroyed by predicted opp leads.
  const benchedLeads = yourSelected.filter(s => !yourLeads.some(l => l.name === s.name));
  for (const back of benchedLeads) {
    for (const opp of oppLeads) {
      const incoming = bestKOAgainst(theirMatrix, opp.name, back.name);
      if (incoming?.hko === 'OHKO') {
        const cmp = isFasterThan(opp, back);
        if (cmp === true) avoid.push(`Don't lead ${back.name} — ${opp.name} outspeeds and OHKOs (${incoming.move} ${incoming.minPct}-${incoming.maxPct}%)`);
      }
    }
  }

  // Lead-vs-lead OHKO warnings (even when we DO lead them — heads-up).
  for (const lead of yourLeads) {
    for (const opp of oppLeads) {
      const incoming = bestKOAgainst(theirMatrix, opp.name, lead.name);
      if (incoming?.hko === 'OHKO') {
        const cmp = isFasterThan(opp, lead);
        if (cmp === true) {
          avoid.push(`${lead.name} risk: ${opp.name} outspeeds and OHKOs with ${incoming.move} (${incoming.minPct}-${incoming.maxPct}%) — switch / Protect turn 1`);
        } else if (cmp === 'tie') {
          avoid.push(`${lead.name} risk: speed-tie ${opp.name}; loses if it wins the roll (${incoming.move} OHKO)`);
        }
      }
    }
  }

  return {
    leads: yourLeads.map(p => p.name),
    why: [...new Set(why)].slice(0, 5),
    avoid: [...new Set(avoid)].slice(0, 4),
  };
}

// ----- Watch-outs (severity-coded) -----
function buildWatchOuts(yourSelected, opponentTeam, oppLikelyBrings, damageEstimates) {
  const out = [];
  const seen = new Set();
  const push = (sev, text) => {
    if (seen.has(text)) return;
    seen.add(text);
    out.push({ severity: sev, text });
  };
  const yourMatrix = damageEstimates?.yourAttacking || [];
  const theirMatrix = damageEstimates?.theirAttacking || [];
  const focus = oppLikelyBrings.length > 0 ? oppLikelyBrings : opponentTeam;

  // OHKO threats — list specific Pokemon and what they OHKO.
  for (const opp of focus) {
    const ohkos = yourSelected.filter(p => {
      const incoming = bestKOAgainst(theirMatrix, opp.name, p.name);
      return incoming?.hko === 'OHKO';
    });
    if (ohkos.length > 0) {
      const ko = bestKOAgainst(theirMatrix, opp.name, ohkos[0].name);
      push('critical', `${opp.name} OHKOs ${ohkos.map(p => p.name).join('/')} with ${ko.move}`);
    }
  }

  // Multiscale / Shadow Shield — "chip first, then nuke".
  for (const opp of focus) {
    if (FIRST_HIT_HALVERS.has(opp.ability)) {
      push('warn', `${opp.name} ${opp.ability} halves first hit at full HP — chip before going for the KO`);
    }
    if (DISGUISE_LIKE.has(opp.ability)) {
      push('warn', `${opp.name} ${opp.ability} eats one hit — don't waste your strongest move`);
    }
    if (STURDY_LIKE.has(opp.ability)) {
      push('info', `${opp.name} Sturdy — guaranteed to survive one OHKO from full HP`);
    }
  }

  // Ability dangers.
  for (const opp of focus) {
    const danger = ABILITY_DANGERS[opp.ability];
    if (danger) push(danger.sev, `${opp.name} ${opp.ability} — ${danger.tip}`);
  }

  // Type immunities our attacker would whiff.
  for (const opp of focus) {
    const immuneType = ABILITY_TYPE_IMMUNITY[opp.ability];
    if (!immuneType) continue;
    const wasters = yourSelected.filter(p => {
      const moves = p.moves || [];
      return moves.some(m => MOVE_TYPES[m] === immuneType && moveCat(m) !== 'Status');
    });
    if (wasters.length > 0) {
      push('warn', `${opp.name} ${opp.ability} blocks ${immuneType} — don't click ${immuneType} on it (${wasters.map(p => p.name).join(', ')})`);
    }
  }

  // Focus Sash on opponents we'd otherwise OHKO.
  for (const opp of focus) {
    if (opp.item !== 'Focus Sash') continue;
    const ohkoUsers = yourSelected.filter(p => {
      const ko = bestKOAgainst(yourMatrix, p.name, opp.name);
      return ko?.hko === 'OHKO';
    });
    if (ohkoUsers.length > 0) push('info', `${opp.name} Focus Sash — survives any OHKO. Chip first or use multi-hit.`);
  }

  // Setup leads (TR, Tailwind, Swords Dance, etc).
  for (const opp of focus) {
    if (isTrSetter(opp.name)) {
      const counters = yourSelected.filter(p =>
        (p.moves || []).includes('Taunt') || (p.moves || []).includes('Fake Out')
      );
      if (counters.length > 0) {
        push('warn', `${opp.name} likely sets Trick Room turn 1 — block with ${counters.map(p => p.name).join(' / ')}`);
      } else {
        push('critical', `${opp.name} likely sets Trick Room turn 1 — no Taunt/Fake Out, must KO it`);
      }
    }
    if (isTailwindUser(opp.name) && (opp.moves || []).includes('Tailwind')) {
      push('warn', `${opp.name} sets Tailwind — match it or punish the setup turn`);
    }
  }

  // Recoil / stat-drop moves on us — only flag if we can't match the trade.
  // (skip — too noisy unless tied to specific damage data)

  // Speed control on opponent (Choice Scarf).
  for (const opp of focus) {
    if (opp.item === 'Choice Scarf') {
      push('info', `${opp.name} likely Choice Scarf — outspeeds but locked into one move`);
    }
  }

  // Mega evolution warnings.
  for (const opp of focus) {
    if (opp.item && isMegaStone(opp.item)) {
      const megaStats = getMegaStats(opp.name);
      if (!megaStats) continue;
      const speedJump = megaStats.spe - (opp.baseStats?.spe || 0);
      if (speedJump >= 30) {
        push('warn', `${opp.name} (${opp.item}) — Mega gains +${speedJump} Speed; fast offense after evo`);
      }
    }
  }

  return out;
}

// ----- Key matchups (threat → answer) -----
function buildKeyMatchups(yourSelected, oppLikelyBrings, damageEstimates) {
  const yourMatrix = damageEstimates?.yourAttacking || [];
  const out = [];
  for (const opp of oppLikelyBrings) {
    const answers = [];
    for (const p of yourSelected) {
      const ko = bestKOAgainst(yourMatrix, p.name, opp.name);
      const tags = getAbilityTags(p.ability);
      const walls = opp.types.every(t => defensiveEffectiveness(t, p.types, tags) <= 0.5);
      if (ko?.hko === 'OHKO') answers.push({ name: p.name, line: `OHKO ${ko.move} (${ko.minPct}-${ko.maxPct}%)`, rank: 4 });
      else if (ko?.hko === '2HKO' && ko.maxPct >= 80) answers.push({ name: p.name, line: `2HKO ${ko.move} (${ko.minPct}-${ko.maxPct}%)`, rank: 3 });
      else if (walls) answers.push({ name: p.name, line: `walls (resists ${opp.types.join('/')})`, rank: 2 });
    }
    if (answers.length === 0) continue;
    answers.sort((a, b) => b.rank - a.rank);
    const top = answers[0];
    out.push({ threat: opp.name, answer: top.name, line: top.line, rank: top.rank });
  }
  out.sort((a, b) => b.rank - a.rank);
  return out;
}

// ----- Per-Pokemon move-suggestion stub for the battlefield UI -----
// The battlefield panel reads dt.turns[0].scenarios[0].response[*].action to
// surface a "click this move" hint per active Pokemon. We keep producing it
// (matrix-driven, no canned templates) so that feature still works.
function buildBattlefieldMoveHints(yourLeads, oppLeads, damageEstimates) {
  const yourMatrix = damageEstimates?.yourAttacking || [];
  const theirMatrix = damageEstimates?.theirAttacking || [];
  const response = [];
  for (const lead of yourLeads) {
    // Pick the highest-priority opp lead to recommend a move against.
    let best = null;
    for (const opp of oppLeads) {
      const ko = bestKOAgainst(yourMatrix, lead.name, opp.name);
      if (!ko) continue;
      if (!best || hkoRank(ko.hko) > hkoRank(best.ko.hko)) best = { opp, ko };
    }
    if (!best) {
      response.push({ pokemon: lead.name, action: 'No projected damage data — pick best STAB' });
      continue;
    }
    const incoming = bestKOAgainst(theirMatrix, best.opp.name, lead.name);
    let note = '';
    if (incoming?.hko === 'OHKO' && isFasterThan(best.opp, lead) === true) note = ' — but they OHKO first, consider switch/Protect';
    response.push({
      pokemon: lead.name,
      action: `${best.ko.move} on ${best.opp.name} (${best.ko.minPct}-${best.ko.maxPct}%${best.ko.hko && best.ko.hko !== 'none' ? ', ' + best.ko.hko : ''})${note}`,
    });
  }
  return response;
}

function generateBattlePlan(yourSelected, yourLeads, opponentTeam, opponentPrediction, damageEstimates, selectCount, ctx = {}) {
  const oppLikelyBrings = (opponentPrediction?.predictions || [])
    .filter(p => p.likely)
    .map(p => opponentTeam.find(o => o.name === p.name))
    .filter(Boolean);
  const oppLeadNames = opponentPrediction?.predictedLeads?.[0]?.leads || [];
  const oppLeads = oppLeadNames.map(n => opponentTeam.find(o => o.name === n)).filter(Boolean);

  const bring = (yourSelected || []).map(p =>
    buildBringEntry(p, opponentTeam, oppLikelyBrings, damageEstimates, p.role, ctx)
  );
  const lead = buildLeadEntry(yourLeads, yourSelected, opponentTeam, opponentPrediction, damageEstimates, selectCount, ctx);
  const watchOuts = buildWatchOuts(yourSelected, opponentTeam, oppLikelyBrings, damageEstimates);
  const keyMatchups = buildKeyMatchups(yourSelected, oppLikelyBrings.length > 0 ? oppLikelyBrings : opponentTeam, damageEstimates);

  // Strategy-driven trigger summary — surface matched triggers as a top-level
  // "why we're bringing this team" block.
  const triggers = (ctx.triggerMatches || []).map(m => ({
    matchedOn: m.matchedOn,
    bring: m.trigger.bring,
    reason: m.trigger.reason || '',
  }));

  // Backwards-compat: keep a single-turn "scenarios" array so the battlefield
  // section can still pull a per-Pokemon move hint.
  const moveHints = buildBattlefieldMoveHints(yourLeads || [], oppLeads, damageEstimates);
  const turns = moveHints.length > 0 ? [{
    turn: 1,
    scenarios: [{
      label: 'Recommended openers',
      probability: 'likely',
      oppAction: oppLeads.length > 0 ? `${oppLeads.map(o => o.name).join(' + ')} on the field` : 'Predicted leads on the field',
      response: moveHints,
      note: null,
    }],
  }] : [];

  return { bring, lead, watchOuts, keyMatchups, triggers, turns };
}

// Legacy entry point — kept for any external callers. Returns just the
// `turns` slice of the battle plan (battlefield move hints).
function generateDecisionTree(yourLeads, oppLeads, damageEstimates, selectCount = 4) {
  const fakeOppPred = {
    predictedLeads: oppLeads.length > 0 ? [{ leads: oppLeads.map(o => o.name) }] : [],
    predictions: oppLeads.map(o => ({ name: o.name, likely: true })),
  };
  const plan = generateBattlePlan(yourLeads, yourLeads, oppLeads, fakeOppPred, damageEstimates, selectCount);
  return { turns: plan.turns };
}


/**
 * Generates turn-1 strategic commentary about what the opponent is likely to try
 * based on their predicted leads vs your suggested leads.
 */
function generateCommentary(yourLeads, opponentPrediction, yourTeam, opponentTeam, selectCount) {
  const tips = [];
  const warnings = [];

  const oppLeadPairs = opponentPrediction?.predictedLeads;
  let topOppLeadNames;
  if (oppLeadPairs && oppLeadPairs.length > 0) {
    topOppLeadNames = oppLeadPairs[0].leads;
  } else {
    // Fallback: use first 2 opponents with highest bring probability
    const preds = opponentPrediction?.predictions || [];
    topOppLeadNames = preds.filter(p => p.likely).slice(0, 2).map(p => p.name);
  }
  if (topOppLeadNames.length === 0) return { tips, warnings };

  const oppLeadPokemon = topOppLeadNames.map(n => opponentTeam.find(p => p.name === n)).filter(Boolean);

  // --- Detect opponent strategies ---

  // Trick Room
  for (const opp of oppLeadPokemon) {
    if (KNOWN_ROLES.trSetters.includes(opp.name)) {
      tips.push({
        type: 'warning',
        text: `${opp.name} will likely try to set Trick Room turn 1.`,
        advice: 'Use Fake Out to block it, Taunt to prevent it, or KO the setter before it moves.',
      });
      // Check if your leads have Taunt or Fake Out
      const hasTaunt = yourLeads.some(p => p.moves?.includes('Taunt'));
      const hasFakeOut = yourLeads.some(p => p.moves?.includes('Fake Out'));
      if (hasFakeOut) {
        tips.push({ type: 'tip', text: `Use Fake Out on ${opp.name} to block Trick Room.` });
      } else if (hasTaunt) {
        tips.push({ type: 'tip', text: `Taunt ${opp.name} to prevent Trick Room.` });
      } else if (!hasFakeOut && !hasTaunt) {
        warnings.push(`You have no Fake Out or Taunt to stop ${opp.name}'s Trick Room. Consider double-targeting it for a KO.`);
      }
    }
  }

  // Tailwind
  for (const opp of oppLeadPokemon) {
    if (KNOWN_ROLES.tailwind.includes(opp.name)) {
      tips.push({
        type: 'warning',
        text: `${opp.name} will likely set Tailwind turn 1 (Prankster makes it priority).`,
        advice: 'Taunt blocks it. Otherwise focus on KOing their damage dealer while Tailwind wastes a turn.',
      });
    }
  }

  // Fake Out
  for (const opp of oppLeadPokemon) {
    if (KNOWN_ROLES.fakeOut.includes(opp.name)) {
      const yourTarget = yourLeads.find(p => {
        // Opponent will Fake Out whoever is more threatening
        const seCount = opponentTeam.filter(o =>
          (p.types || []).some(t => getEffectiveness(t, o.types) >= 2)
        ).length;
        return seCount > 0;
      });
      if (yourTarget) {
        tips.push({
          type: 'warning',
          text: `${opp.name} will likely Fake Out your ${yourTarget.name} to stop it from acting turn 1.`,
          advice: 'Protect on the Fake Out target wastes their Fake Out. Or lead something with Armor Tail/Psychic Terrain.',
        });
      } else {
        tips.push({
          type: 'info',
          text: `${opp.name} has Fake Out — expect one of your leads to be flinched turn 1.`,
        });
      }
    }
  }

  // Weather setter
  for (const opp of oppLeadPokemon) {
    if (KNOWN_ROLES.weatherSetters.includes(opp.name)) {
      const weatherMap = {
        'Torkoal': 'Sun', 'Ninetales': 'Sun',
        'Pelipper': 'Rain', 'Politoed': 'Rain',
        'Tyranitar': 'Sand', 'Hippowdon': 'Sand',
        'Abomasnow': 'Snow', 'Ninetales-Alola': 'Snow',
      };
      const weather = weatherMap[opp.name] || 'weather';
      tips.push({
        type: 'info',
        text: `${opp.name} sets ${weather} on entry — their team likely has ${weather} abusers in the back.`,
        advice: `Override with your own weather setter if available, or KO the ${weather}-dependent sweeper.`,
      });
    }
  }

  // Intimidate
  for (const opp of oppLeadPokemon) {
    if (KNOWN_ROLES.intimidators.includes(opp.name)) {
      const yourPhysical = yourLeads.filter(p => {
        // Rough check: if STAB types tend to be physical
        return true; // always relevant
      });
      tips.push({
        type: 'info',
        text: `${opp.name} Intimidate will lower your leads' Attack on entry.`,
        advice: 'Special attackers ignore Intimidate. Competitive/Defiant Pokemon benefit from it.',
      });
      const hasCompetitive = yourLeads.some(p =>
        p.ability === 'Competitive' || p.ability === 'Defiant' || p.ability === 'Clear Body' || p.ability === 'Mirror Armor'
      );
      if (hasCompetitive) {
        const who = yourLeads.find(p =>
          ['Competitive','Defiant','Clear Body','Mirror Armor'].includes(p.ability)
        );
        tips.push({ type: 'tip', text: `${who.name}'s ${who.ability} punishes their Intimidate!` });
      }
    }
  }

  // Redirection
  for (const opp of oppLeadPokemon) {
    if (KNOWN_ROLES.redirectors.includes(opp.name)) {
      tips.push({
        type: 'warning',
        text: `${opp.name} can redirect your attacks with Follow Me / Rage Powder.`,
        advice: 'Use spread moves (Earthquake, Heat Wave, Dazzling Gleam) to bypass redirection, or target the redirector directly.',
      });
    }
  }

  // Bad type matchup → likely switches/pivots
  for (const opp of oppLeadPokemon) {
    const badMatchups = yourLeads.filter(yours =>
      yours.types.some(t => getEffectiveness(t, opp.types) >= 2)
    );
    if (badMatchups.length >= 2) {
      tips.push({
        type: 'tip',
        text: `${opp.name} is weak to both your leads — it may switch out, Protect, or use a pivot move (U-turn, Volt Switch, Parting Shot).`,
        advice: 'Predict the switch and target the incoming Pokemon, or use the free turn to set up.',
      });
    }
  }

  // Your leads SE on both opponents — you have strong position
  const bothOppThreatened = oppLeadPokemon.every(opp =>
    yourLeads.some(yours =>
      getOffensiveTypes(yours).some(t => getEffectiveness(t, opp.types) >= 2)
    )
  );
  if (bothOppThreatened && oppLeadPokemon.length === 2) {
    tips.push({
      type: 'tip',
      text: `Your leads threaten both ${topOppLeadNames[0]} and ${topOppLeadNames[1]} super effectively — you have strong turn 1 pressure.`,
      advice: 'Go aggressive. They may be forced into Protect or switches.',
    });
  }

  // Your leads are both weak to an opp lead — danger
  for (const opp of oppLeadPokemon) {
    const bothWeak = yourLeads.filter(yours =>
      opp.types.some(t => getEffectiveness(t, yours.types) >= 2)
    );
    if (bothWeak.length >= 2) {
      warnings.push(`${opp.name} hits both your leads super effectively. Consider protecting one and doubling into ${opp.name}, or switching to a resist.`);
    }
  }

  // Priority blocker on opponent side
  for (const opp of oppLeadPokemon) {
    if (KNOWN_ROLES.priorityBlockers.includes(opp.name)) {
      const yourPriorityUser = yourLeads.find(p =>
        p.moves?.some(m => ['Fake Out','Extreme Speed','Aqua Jet','Bullet Punch','Ice Shard','Sucker Punch','Mach Punch','Grassy Glide','Shadow Sneak','Quick Attack'].includes(m))
      );
      if (yourPriorityUser) {
        warnings.push(`${opp.name} blocks priority moves — your ${yourPriorityUser.name}'s priority moves won't work while it's on the field.`);
      }
    }
  }

  // ---- Ability-aware tips ----

  // Opponent defensive abilities: Multiscale, Disguise, Sturdy
  for (const opp of oppLeadPokemon) {
    const oppAbility = opp.ability || '';
    if (FULL_HP_SHIELDS.includes(oppAbility)) {
      tips.push({
        type: 'warning',
        text: `${opp.name} has ${oppAbility} — damage is halved at full HP.`,
        advice: `Don't waste your strongest attack first. Chip with a weaker/spread move to break the shield, then nuke.`,
      });
    }
    if (DISGUISE_ABILITIES.includes(oppAbility)) {
      tips.push({
        type: 'warning',
        text: `${opp.name} has ${oppAbility} — the first hit is completely blocked.`,
        advice: `Break it with a weak attack, multi-hit move, or spread move. Save your nuke for after it's broken.`,
      });
    }
    if (STURDY_ABILITIES.includes(oppAbility)) {
      tips.push({
        type: 'info',
        text: `${opp.name} has Sturdy — it can't be OHKOd from full HP.`,
        advice: `Use a multi-hit move (Population Bomb, Triple Axel) or chip first. Sandstorm/Hail chip also breaks Sturdy.`,
      });
    }
  }

  // Opponent ability immunities vs your lead moves
  for (const opp of oppLeadPokemon) {
    const immuneType = getAbilityImmunity(opp);
    if (!immuneType) continue;
    for (const yours of yourLeads) {
      const blockedMoves = (yours.moves || []).filter(m => getMoveType(m) === immuneType);
      if (blockedMoves.length > 0) {
        warnings.push(
          `${opp.name}'s ${opp.ability} absorbs ${immuneType}-type moves — don't use ${yours.name}'s ${blockedMoves.join('/')} into it.`
        );
      }
    }
  }

  // Opponent Magic Bounce: reflects status moves
  for (const opp of oppLeadPokemon) {
    if (opp.ability === 'Magic Bounce') {
      const statusMoves = yourLeads.flatMap(p =>
        (p.moves || []).filter(m => ['Taunt','Will-O-Wisp','Thunder Wave','Spore','Sleep Powder','Toxic','Stealth Rock','Encore','Leech Seed'].includes(m))
          .map(m => `${p.name}'s ${m}`)
      );
      if (statusMoves.length > 0) {
        warnings.push(`${opp.name} has Magic Bounce — ${statusMoves.join(', ')} will bounce back at you!`);
      }
    }
  }

  // Opponent Unaware: ignores your stat boosts
  for (const opp of oppLeadPokemon) {
    if (opp.ability === 'Unaware') {
      const setupUsers = yourLeads.filter(p =>
        (p.moves || []).some(m => ['Swords Dance','Nasty Plot','Dragon Dance','Calm Mind','Quiver Dance','Shell Smash','Bulk Up','Coil'].includes(m))
      );
      if (setupUsers.length > 0) {
        tips.push({
          type: 'warning',
          text: `${opp.name} has Unaware — it ignores stat boosts from ${setupUsers.map(p => p.name).join('/')}.`,
          advice: `Don't waste turns setting up while ${opp.name} is on the field. KO it first, then set up.`,
        });
      }
    }
  }

  // Your abilities: note valuable defensive abilities
  for (const yours of yourLeads) {
    const ability = yours.ability || '';

    // Your Multiscale / Shadow Shield
    if (FULL_HP_SHIELDS.includes(ability)) {
      tips.push({
        type: 'tip',
        text: `${yours.name}'s ${ability} halves damage while at full HP — it can tank a big hit turn 1.`,
        advice: `Don't switch ${yours.name} in and out carelessly. Protect if needed to scout, but staying in at full HP has massive value.`,
      });
    }

    // Your absorption abilities — can redirect/absorb attacks
    const absorbType = ABILITY_IMMUNITIES[ability];
    if (absorbType) {
      const oppWithType = oppLeadPokemon.filter(opp =>
        (opp.moves || []).some(m => getMoveType(m) === absorbType) || opp.types.includes(absorbType)
      );
      if (oppWithType.length > 0) {
        tips.push({
          type: 'tip',
          text: `${yours.name}'s ${ability} absorbs ${absorbType}-type moves from ${oppWithType.map(p => p.name).join('/')}.`,
          advice: `Position ${yours.name} to draw ${absorbType} attacks. In doubles, your partner can safely ignore ${absorbType} threats.`,
        });
      }
    }

    // Zero to Hero (Palafin) — must switch out and back to transform
    if (ability === 'Zero to Hero') {
      tips.push({
        type: 'tip',
        text: `${yours.name} needs to switch out and back in to activate Zero to Hero (Hero form).`,
        advice: `Lead ${yours.name}, use Fake Out or a quick move, then switch out. When it returns, it has massively boosted stats.`,
      });
    }
  }

  // Mega evolution timing advice in commentary
  for (const yours of yourLeads) {
    if (!canMegaEvolve(yours)) continue;
    const timing = analyseMegaTiming(yours, oppLeadPokemon, { yourAttacking: [], theirAttacking: [] });
    if (!timing) continue;

    if (timing.shouldDelay && timing.delayReasons.length > 0) {
      tips.push({
        type: 'tip',
        text: `Consider delaying ${yours.name}'s Mega Evolution.`,
        advice: timing.delayReasons[0],
      });
    } else if (timing.shouldMegaNow && timing.immediateReasons.length > 0) {
      tips.push({
        type: 'tip',
        text: `Mega Evolve ${yours.name} immediately.`,
        advice: timing.immediateReasons[0],
      });
    } else if (timing.isConflicted) {
      tips.push({
        type: 'info',
        text: `${yours.name} Mega Evolution timing is a judgment call.`,
        advice: `Delay: ${timing.delayReasons[0]}. Now: ${timing.immediateReasons[0]}`,
      });
    }
  }

  return { tips, warnings };
}

// Known role lookups for opponent Pokemon
const KNOWN_ROLES = {
  intimidators: ['Incineroar','Arcanine','Gyarados','Landorus','Krookodile','Luxray','Salamence'],
  redirectors: ['Amoonguss','Indeedee-F','Clefable','Togekiss','Sinistcha'],
  fakeOut: ['Incineroar','Rillaboom','Mienshao','Hitmontop','Scrafty','Sneasler','Ambipom','Maushold'],
  trSetters: ['Porygon2','Hatterene','Dusclops','Cresselia','Farigiraf','Oranguru','Sinistcha','Gothitelle'],
  tailwind: ['Whimsicott','Tornadus','Talonflame','Murkrow','Pelipper'],
  weatherSetters: ['Torkoal','Pelipper','Tyranitar','Politoed','Ninetales','Ninetales-Alola','Abomasnow','Hippowdon'],
  priorityBlockers: ['Farigiraf','Tsareena','Bruxish'],
};

/**
 * Predicts which Pokemon the opponent is likely to bring and lead with.
 * Incorporates learned adjustments from match history.
 */
function predictOpponentSelection(yourTeam, opponentTeam, selectCount, learnedAdjustments = {}) {
  const oppSelectCount = selectCount;

  // Score each opponent Pokemon
  const scored = opponentTeam.map(opp => {
    let score = 0;
    const reasons = [];
    let leadScore = 0; // separate score for lead likelihood
    const leadReasons = [];

    // --- Matchup-based scoring ---
    for (const yours of yourTeam) {
      let bestOffense = 0;
      for (const atkType of opp.types) {
        bestOffense = Math.max(bestOffense, getEffectiveness(atkType, yours.types));
      }
      if (bestOffense >= 4) score += 4;
      else if (bestOffense >= 2) score += 3;
      else if (bestOffense >= 1) score += 1;
      else if (bestOffense === 0) score -= 1;

      let worstDef = 1;
      for (const yourType of yours.types) {
        worstDef = Math.max(worstDef, getEffectiveness(yourType, opp.types));
      }
      if (worstDef <= 0.5) score += 1.5;
      else if (worstDef === 0) score += 2;
      else if (worstDef >= 2) score -= 1;
    }

    // --- Threat/wall reasons ---
    const threatens = yourTeam.filter(y => opp.types.some(t => getEffectiveness(t, y.types) >= 2));
    if (threatens.length > 0) reasons.push(`Threatens ${threatens.map(p => p.name).join(', ')}`);

    const walls = yourTeam.filter(y => y.types.every(t => getEffectiveness(t, opp.types) < 1));
    if (walls.length > 0) reasons.push(`Walls ${walls.map(p => p.name).join(', ')}`);

    // --- Role-based scoring + lead scoring ---
    if (KNOWN_ROLES.intimidators.includes(opp.name)) {
      score += 2; reasons.push('Likely Intimidate support');
      leadScore += 3; leadReasons.push('Intimidate strongest on turn 1');
    }
    if (KNOWN_ROLES.fakeOut.includes(opp.name)) {
      score += 1; reasons.push('Likely has Fake Out');
      leadScore += 4; leadReasons.push('Fake Out is a lead-only move');
    }
    if (KNOWN_ROLES.redirectors.includes(opp.name)) {
      score += 1; reasons.push('Likely redirection support');
      leadScore += 1; leadReasons.push('May redirect to protect partner');
    }
    if (KNOWN_ROLES.trSetters.includes(opp.name)) {
      score += 1; reasons.push('Potential Trick Room setter');
      leadScore += 3; leadReasons.push('TR must be set early to matter');
    }
    if (KNOWN_ROLES.tailwind.includes(opp.name)) {
      score += 1; reasons.push('Likely Tailwind setter');
      leadScore += 3; leadReasons.push('Tailwind needs turn 1 setup');
    }
    if (KNOWN_ROLES.weatherSetters.includes(opp.name)) {
      score += 1; reasons.push('Weather setter');
      leadScore += 2; leadReasons.push('Weather best set on entry');
    }
    if (KNOWN_ROLES.priorityBlockers.includes(opp.name)) {
      leadScore += 2; leadReasons.push('Blocks opponent Fake Out — protects partner lead');
    }

    // Offensive threats with wide SE coverage are good leads
    const seCount = yourTeam.filter(y => opp.types.some(t => getEffectiveness(t, y.types) >= 2)).length;
    if (seCount >= 3) { leadScore += 2; leadReasons.push(`Hits ${seCount} of your team SE — immediate pressure`); }

    // Endgame Pokemon are bad leads
    const endgameNames = ['Kingambit']; // Supreme Overlord wants to come in last
    if (endgameNames.includes(opp.name)) {
      leadScore -= 3; leadReasons.push('Supreme Overlord strongest late — unlikely lead');
    }

    // --- Designated Mega: heavy bring bump ---
    // A Pokemon holding a Mega Stone is the opponent's strategic cornerstone
    // and is brought in the vast majority of matches. Without this, Kangaskhan
    // (low raw type coverage) was scoring ~9% against Water/Steel-heavy teams
    // when it's actually a near-certain bring.
    const isDesignatedMega = !!opp.megaStats || (canMegaEvolve(opp) && isMegaStone(opp.item));
    if (isDesignatedMega) {
      score += 8;
      reasons.push('Holding Mega Stone — central to opponent strategy');
    }

    return { name: opp.name, types: opp.types, score, reasons, leadScore, leadReasons };
  });

  scored.sort((a, b) => b.score - a.score);

  // --- Apply learned adjustments ---
  const withLearning = scored.map((s, i) => {
    const learned = learnedAdjustments[s.name];
    let adjustedScore = s.score;
    let adjustedLeadScore = s.leadScore;
    const extraReasons = [];
    const extraLeadReasons = [];

    if (learned && learned.sampleSize >= 2) {
      const n = learned.sampleSize;
      // Blend: weight learning more as sample grows (max ~60% influence at 20+ samples)
      const learnWeight = Math.min(0.6, n * 0.03);

      // Bring rate adjustment
      if (learned.bringRate > 0.7) {
        adjustedScore += 3 * learnWeight;
        extraReasons.push(`Brought ${Math.round(learned.bringRate * 100)}% of the time (${n} matches)`);
      } else if (learned.bringRate > 0.5) {
        adjustedScore += 1.5 * learnWeight;
        extraReasons.push(`Brought ${Math.round(learned.bringRate * 100)}% (${n} matches)`);
      } else if (learned.bringRate < 0.3 && n >= 3) {
        adjustedScore -= 2 * learnWeight;
        extraReasons.push(`Only brought ${Math.round(learned.bringRate * 100)}% (${n} matches) — often benched`);
      }

      // Lead rate adjustment
      if (learned.leadRate > 0.6) {
        adjustedLeadScore += 3 * learnWeight;
        extraLeadReasons.push(`Led ${Math.round(learned.leadRate * 100)}% when brought (${n} matches)`);
      } else if (learned.leadRate < 0.15 && n >= 3) {
        adjustedLeadScore -= 2 * learnWeight;
        extraLeadReasons.push(`Rarely leads (${Math.round(learned.leadRate * 100)}%, ${n} matches)`);
      }
    }

    return {
      ...s,
      score: adjustedScore,
      leadScore: adjustedLeadScore,
      reasons: [...s.reasons, ...extraReasons],
      leadReasons: [...s.leadReasons, ...extraLeadReasons],
    };
  });

  // Re-sort after learning adjustments
  withLearning.sort((a, b) => b.score - a.score);

  // --- Compute bring probabilities ---
  const totalScore = withLearning.reduce((sum, s) => sum + Math.max(s.score, 0.1), 0);
  const withProbability = withLearning.map((s, i) => {
    const rawProb = Math.max(s.score, 0.1) / totalScore;
    const isLikelyBring = i < oppSelectCount;
    const adjustedProb = isLikelyBring
      ? Math.min(0.95, 0.5 + rawProb * 2)
      : Math.max(0.05, rawProb * 0.8);

    return {
      ...s,
      probability: Math.round(adjustedProb * 100),
      likely: isLikelyBring,
    };
  });

  // --- Predict opponent leads (from the likely-bring pool) ---
  const likelyBrings = withProbability.filter(p => p.likely);
  let predictedLeads = null;

  if (selectCount === 4 && likelyBrings.length >= 2) {
    const leadPairs = [];
    for (let i = 0; i < likelyBrings.length; i++) {
      for (let j = i + 1; j < likelyBrings.length; j++) {
        const a = likelyBrings[i];
        const b = likelyBrings[j];
        let pairScore = a.leadScore + b.leadScore;
        const pairReasons = [];

        // Fake Out + setup/attacker
        const aFake = KNOWN_ROLES.fakeOut.includes(a.name);
        const bFake = KNOWN_ROLES.fakeOut.includes(b.name);
        if (aFake && !bFake) { pairScore += 3; pairReasons.push(`${a.name} Fake Out protects ${b.name}`); }
        else if (bFake && !aFake) { pairScore += 3; pairReasons.push(`${b.name} Fake Out protects ${a.name}`); }
        else if (aFake && bFake) { pairScore -= 1; pairReasons.push('Both Fake Out — redundant lead'); }

        // Weather setter + abuser (opponent side)
        const aWeather = KNOWN_ROLES.weatherSetters.includes(a.name);
        const bWeather = KNOWN_ROLES.weatherSetters.includes(b.name);
        if (aWeather && !bWeather) { pairScore += 2; pairReasons.push(`${a.name} sets weather for team`); }
        else if (bWeather && !aWeather) { pairScore += 2; pairReasons.push(`${b.name} sets weather for team`); }

        // TR setter + slow attacker
        const aTR = KNOWN_ROLES.trSetters.includes(a.name);
        const bTR = KNOWN_ROLES.trSetters.includes(b.name);
        if (aTR && !bTR) { pairScore += 2; pairReasons.push(`${a.name} sets TR while ${b.name} attacks`); }
        else if (bTR && !aTR) { pairScore += 2; pairReasons.push(`${b.name} sets TR while ${a.name} attacks`); }

        // Tailwind + attacker
        const aTW = KNOWN_ROLES.tailwind.includes(a.name);
        const bTW = KNOWN_ROLES.tailwind.includes(b.name);
        if (aTW && !bTW) { pairScore += 2; pairReasons.push(`${a.name} Tailwind + ${b.name} sweeps`); }
        else if (bTW && !aTW) { pairScore += 2; pairReasons.push(`${b.name} Tailwind + ${a.name} sweeps`); }

        // Intimidate value
        const aIntim = KNOWN_ROLES.intimidators.includes(a.name);
        const bIntim = KNOWN_ROLES.intimidators.includes(b.name);
        if (aIntim || bIntim) {
          const who = aIntim ? a.name : b.name;
          pairReasons.push(`${who} Intimidate on entry`);
        }

        // Collect unique lead reasons from individuals
        const allIndivReasons = [...a.leadReasons, ...b.leadReasons]
          .filter(r => !pairReasons.some(pr => pr.includes(r)));

        leadPairs.push({
          leads: [a.name, b.name],
          back: likelyBrings.filter(p => p.name !== a.name && p.name !== b.name).map(p => p.name),
          score: Math.round(pairScore * 10) / 10,
          reasons: [...pairReasons, ...allIndivReasons.slice(0, 2)],
        });
      }
    }
    leadPairs.sort((a, b) => b.score - a.score);
    predictedLeads = leadPairs;
  } else if (selectCount === 3 && likelyBrings.length >= 1) {
    // Singles: rank leads by leadScore
    predictedLeads = likelyBrings
      .sort((a, b) => b.leadScore - a.leadScore)
      .map(p => ({
        leads: [p.name],
        score: p.leadScore,
        reasons: p.leadReasons,
      }));
  }

  return { predictions: withProbability, predictedLeads };
}

export const ALL_MOVES = Object.keys(MOVE_TYPES).sort();
